// src/pages/PerformancePage.js
// Phase 2: Performance — Zero Squad | Power Rankings | Category Rankings | Scorecards
// With store/TL/group filtering, simple-average scoring, Excel downloads

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ScrollableTableWrapper } from './SalesSummaryBreakdown';

// Downloads use responseType:'blob', so a failed request's error body also
// arrives as a Blob rather than parsed JSON — this reads the actual
// message back out of it, falling back to something reasonable otherwise.
async function blobErrorMessage(err, fallback) {
  if (err?.response?.data instanceof Blob) {
    try {
      const text = await err.response.data.text();
      const parsed = JSON.parse(text);
      if (parsed?.error) return parsed.error;
    } catch { /* not JSON — fall through to fallback */ }
  }
  return err?.response?.data?.error || fallback;
}

const C = {
  blue:'#2563EB', blueDark:'#1E40AF', teal:'#06B6D4',
  gray50:'#F8FAFC', gray100:'#F1F5F9', gray200:'#E2E8F0',
  gray400:'#94A3B8', gray600:'#475569', gray800:'#1E293B', gray900:'#0F172A',
  white:'#FFFFFF', success:'#059669', warning:'#D97706', danger:'#DC2626',
};
const fmt = v => (v==null||isNaN(v))?'—':Math.round(Number(v)).toLocaleString();
const RS = {
  'Exceeding Expectations':{bg:'#D1FAE5',fg:'#065F46',icon:'🏆'},
  'Meets Expectations':{bg:'#DBEAFE',fg:'#1E40AF',icon:'✅'},
  'Improvement Needed':{bg:'#FEF3C7',fg:'#92400E',icon:'⚠️'},
  'Unsatisfactory':{bg:'#FEE2E2',fg:'#991B1B',icon:'🔴'},
};
function gapA(v){if(v>0)return{i:'▲',c:C.success};if(v<0)return{i:'▼',c:C.danger};return{i:'▶',c:C.warning};}
function Badge({rating}){const s=RS[rating]||{bg:C.gray100,fg:C.gray600,icon:'—'};return<span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'3px 10px',borderRadius:8,background:s.bg,color:s.fg,fontWeight:800,fontSize:12}}>{s.icon} {rating}</span>;}

const CAT_KEYS = ['PORT','SIMS','POSTPAID','POSTPAID_ACCEL','DIGI_PLUS','DPLUS_ACCEL','ACCESSORIES','PHONES','PHONES_ACCEL','PLANS'];
const CAT_LABELS = {PORT:'Port',SIMS:'Prepaid',POSTPAID:'Postpaid',POSTPAID_ACCEL:'Postpaid Accel',DIGI_PLUS:'D+',DPLUS_ACCEL:'D+ Accel',ACCESSORIES:'Accessories',PHONES:'Phones',PHONES_ACCEL:'Phones Accel',PLANS:'Plans'};

// "MTD" (month-to-date) only means anything for a month that's still in
// progress -- once a month is fully closed there's no partial-progress
// figure left to show, the full month's target is simply what the whole
// month's actual sales get compared against. A comma-joined multi-period
// selection counts as past only if EVERY period in it is before the
// current month; if the current month is part of the selection, MTD still
// has a meaningful partial figure to show for that part of it.
function isPastPeriod(period) {
  if (!period) return false;
  const now = new Date();
  const currentYYYYMM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return period.split(',').every(p => p < currentYYYYMM);
}

// Shared % of Target / % of MTD Target toggle used across every Performance
// tab -- for a past period this collapses to a plain, non-interactive "%
// of Target" label instead of a two-way toggle, since there's nothing
// meaningful left to switch to.
function PctModeToggle({ pctMode, setPctMode, period }) {
  if (isPastPeriod(period)) {
    return (
      <div style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: C.white, color: C.blue }}>% of Target</div>
    );
  }
  return (
    <>
      <button onClick={()=>setPctMode('target')} style={{padding:'4px 10px',borderRadius:6,border:'none',fontSize:11,fontWeight:700,cursor:'pointer',background:pctMode==='target'?C.white:'transparent',color:pctMode==='target'?C.blue:C.gray400}}>% of Target</button>
      <button onClick={()=>setPctMode('mtd')} style={{padding:'4px 10px',borderRadius:6,border:'none',fontSize:11,fontWeight:700,cursor:'pointer',background:pctMode==='mtd'?C.white:'transparent',color:pctMode==='mtd'?C.blue:C.gray400}}>% of MTD Target</button>
    </>
  );
}

// Matches the exact sales-category labels and behavioral subheading names
// the appraisal side's notesForItem() matches tags against — picking one
// here rather than free-typing a tag reliably connects the note to every
// item under that heading.
const NOTE_TAG_OPTIONS = [
  { group: 'Sales', options: ['Port', 'Sim with Plan', 'Sim Only', 'Postpaid', 'Digicel+', 'Accessories', 'Handsets', 'Prepaid Plans'] },
  { group: 'Behavioral', options: ['Communication Skills', 'Work Ethics', 'Reliability', 'Teamwork', 'Safety Policies and Procedures', 'Attendance', 'Punctuality'] },
];

// Sortable header hook
function useSortable(defaultKey, defaultDir='desc') {
  const [sortKey,setSortKey]=useState(defaultKey);
  const [sortDir,setSortDir]=useState(defaultDir);
  const toggle=(key)=>{
    if(sortKey===key) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortKey(key); setSortDir(defaultDir); }
  };
  const arrow=(key)=>sortKey===key?(sortDir==='asc'?' ↑':' ↓'):'';
  return {sortKey,sortDir,toggle,arrow};
}
function sortBy(arr,key,dir,getter) {
  return [...arr].sort((a,b)=>{
    const va=getter?getter(a):a[key], vb=getter?getter(b):b[key];
    if(va==null&&vb==null)return 0; if(va==null)return 1; if(vb==null)return -1;
    const cmp=typeof va==='string'?va.localeCompare(vb):va-vb;
    return dir==='asc'?cmp:-cmp;
  });
}

// ── Filter Bar ───────────────────────────────────────────────────────────────
function FilterBar({data, filter, setFilter}) {
  const [showStoreFilter, setShowStoreFilter] = useState(false);
  const [checkedStores, setCheckedStores] = useState([]);

  const allStores = useMemo(() => [...new Set((data?.agents||[]).map(a=>a.storeCode))].sort(), [data]);
  const allTLs = useMemo(() => [...new Set((data?.agents||[]).map(a=>a.teamLead).filter(Boolean))].sort(), [data]);

  const applyStoreFilter = () => {
    if (checkedStores.length === 0) setFilter({type:'all'});
    else setFilter({type:'stores',stores:checkedStores});
    setShowStoreFilter(false);
  };

  const clearStoreFilter = () => {
    setCheckedStores([]);
    setFilter({type:'all'});
    setShowStoreFilter(false);
  };

  return (
    <div style={{marginBottom:14,padding:'8px 0',borderBottom:`1px solid ${C.gray100}`}}>
      <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontSize:12,fontWeight:700,color:C.gray400}}>Filter:</span>
        <button onClick={()=>{setFilter({type:'all'});setCheckedStores([]);}} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${filter.type==='all'?C.blue:C.gray200}`,background:filter.type==='all'?'#EFF6FF':C.white,color:filter.type==='all'?C.blue:C.gray600,fontSize:12,fontWeight:600,cursor:'pointer'}}>All</button>
        {allTLs.map(tl=>(
          <button key={tl} onClick={()=>{setFilter({type:'tl',value:tl});setCheckedStores([]);}} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${filter.type==='tl'&&filter.value===tl?C.blue:C.gray200}`,background:filter.type==='tl'&&filter.value===tl?'#EFF6FF':C.white,color:filter.type==='tl'&&filter.value===tl?C.blue:C.gray600,fontSize:12,fontWeight:600,cursor:'pointer'}}>
            👤 {tl}
          </button>
        ))}
        <button onClick={()=>setShowStoreFilter(s=>!s)} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${filter.type==='store'||filter.type==='stores'?C.blue:C.gray200}`,background:filter.type==='store'||filter.type==='stores'?'#EFF6FF':C.white,color:filter.type==='store'||filter.type==='stores'?C.blue:C.gray600,fontSize:12,fontWeight:600,cursor:'pointer'}}>
          🏪 Stores {checkedStores.length > 0 ? `(${checkedStores.length})` : ''}
        </button>
      </div>

      {showStoreFilter && (
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center',marginTop:8,padding:'8px 12px',background:C.gray50,borderRadius:10,border:`1px solid ${C.gray200}`}}>
          {allStores.map(s=>(
            <label key={s} style={{display:'flex',alignItems:'center',gap:3,fontSize:12,cursor:'pointer',padding:'3px 6px',borderRadius:6,background:checkedStores.includes(s)?'#EFF6FF':C.white,border:`1px solid ${checkedStores.includes(s)?C.blue:C.gray200}`}}>
              <input type="checkbox" checked={checkedStores.includes(s)} onChange={e=>{
                setCheckedStores(prev=>e.target.checked?[...prev,s]:prev.filter(x=>x!==s));
              }} style={{margin:0}} />
              {s.replace('DIGIWIRE','DW')}
            </label>
          ))}
          <button onClick={applyStoreFilter} style={{padding:'4px 12px',borderRadius:6,border:'none',background:C.blue,color:C.white,fontSize:11,fontWeight:700,cursor:'pointer'}}>Apply</button>
          <button onClick={clearStoreFilter} style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${C.gray200}`,background:C.white,color:C.gray400,fontSize:11,cursor:'pointer'}}>Clear</button>
        </div>
      )}
    </div>
  );
}

function useFiltered(data, filter) {
  return useMemo(() => {
    if (!data?.agents) return [];
    let agents = data.agents;
    if (filter.type === 'tl') agents = agents.filter(a => a.teamLead === filter.value);
    else if (filter.type === 'store') agents = agents.filter(a => a.storeCode === filter.value);
    else if (filter.type === 'stores' && filter.stores) agents = agents.filter(a => filter.stores.includes(a.storeCode));
    return agents;
  }, [data, filter]);
}

function DownloadButtons({period, filter, type, extraParams}) {
  const { can } = useAuth();
  const dl = async (scope) => {
    const params = new URLSearchParams({type});
    if (extraParams) { for (const [k,v] of new URLSearchParams(extraParams)) params.append(k, v); }
    if (scope === 'filtered' && filter.type !== 'all') {
      let stores = [];
      if (filter.type === 'store') stores = [filter.value];
      else if (filter.type === 'stores' && filter.stores) stores = filter.stores;
      if (stores.length > 0) params.append('stores', stores.join(','));
      if (filter.type === 'tl' && filter.value) params.append('tl', filter.value);
    }
    try {
      const res = await axios.get(`/api/performance/report/${period}?${params}`, {responseType:'blob'});
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.setAttribute('download', `${type}_report.xlsx`);
      document.body.appendChild(a); a.click(); a.remove();
    } catch(e) { toast.error(await blobErrorMessage(e, 'Download failed. Please try again.')); }
  };
  if (can('performance.download') !== 'true') return null;
  return (
    <div style={{display:'flex',gap:4}}>
      <button onClick={()=>dl('filtered')} style={{padding:'5px 10px',borderRadius:6,border:'none',background:'linear-gradient(135deg,#059669,#047857)',color:C.white,fontWeight:700,fontSize:11,cursor:'pointer'}}>📥 Download Filtered</button>
      <button onClick={()=>dl('all')} style={{padding:'5px 10px',borderRadius:6,border:`1px solid ${C.gray200}`,background:C.white,color:C.gray600,fontWeight:600,fontSize:11,cursor:'pointer'}}>📥 Download All</button>
    </div>
  );
}

// ── ZERO SQUAD ───────────────────────────────────────────────────────────────
function ZeroSquadTab({agents,thresholds,onUpdateThresholds,isSM,period,filter,thresholdMode,excludeDormant,includeZeroSales,bands}) {
  const [showOnly,setShowOnly]=useState(true);
  const [showSettings,setShowSettings]=useState(false);
  const [localThr,setLocalThr]=useState({});
  const [roleFilter,setRoleFilter]=useState('all');
  const [catFilter,setCatFilter]=useState([]); // multi-select — empty = all categories (condensed view)
  const [pctMode,setPctMode]=useState(thresholdMode||'target');
  useEffect(() => { if (isPastPeriod(period)) setPctMode('target'); }, [period]);
  const {sortKey,sortDir,toggle,arrow}=useSortable('overallPct','asc');
  useEffect(()=>{setLocalThr({...thresholds});},[thresholds]);

  const getCatRating = (pct) => { const s=[...(bands||[])].sort((a,b)=>b.min_pct-a.min_pct); for(const b of s){const max=b.max_pct!=null?b.max_pct:Infinity;if(pct>=b.min_pct&&pct<=max)return b.label;} return s.length?s[s.length-1].label:'Unrated'; };
  const toggleCat = (k) => setCatFilter(prev => prev.includes(k) ? prev.filter(x=>x!==k) : [...prev, k]);

  // Recompute flags locally based on the selected mode (independent of Settings default)
  const flagged = useMemo(() => agents.map(a => {
    const zeroSquadFlags = [];
    for (const cat of a.categories) {
      const thr = thresholds[cat.key] || 60;
      const pct = pctMode === 'mtd' ? (cat.pctOfMtdTarget ?? 0) : cat.pctOfTarget;
      const ref = pctMode === 'mtd' ? cat.mtdTarget : cat.target;
      if (ref > 0 && pct < thr) zeroSquadFlags.push(cat.key);
    }
    return { ...a, zeroSquadFlags, isZeroSquad: zeroSquadFlags.length > 0 };
  }), [agents, thresholds, pctMode]);

  const list = useMemo(()=>{
    let l = [...flagged];
    if(roleFilter==='staff') l = l.filter(a=>a.role!=='Supervisor');
    else if(roleFilter==='supervisor') l = l.filter(a=>a.role==='Supervisor');
    if(catFilter.length>0) l = l.filter(a=>a.zeroSquadFlags?.some(f=>catFilter.includes(f)));
    if(showOnly) l = l.filter(a=>a.isZeroSquad);
    if(sortKey==='agentName') return sortBy(l,'agentName',sortDir);
    if(sortKey==='storeCode') return sortBy(l,'storeCode',sortDir);
    if(sortKey==='overallPct') return sortBy(l,'overallPct',sortDir);
    return sortBy(l,sortKey,sortDir,a=>{const c=a.categories.find(x=>x.key===sortKey);return c?(pctMode==='mtd'?(c.pctOfMtdTarget??0):c.pctOfTarget):0;});
  },[flagged,showOnly,sortKey,sortDir,roleFilter,catFilter]);

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,flexWrap:'wrap',gap:6}}>
        <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
          <label style={{display:'flex',alignItems:'center',gap:4,fontSize:13,fontWeight:600,color:C.gray600,cursor:'pointer'}}>
            <input type="checkbox" checked={showOnly} onChange={e=>setShowOnly(e.target.checked)} /> Flagged only
          </label>
          <span style={{fontSize:13,fontWeight:800,color:C.danger}}>🔴 {flagged.filter(a=>a.isZeroSquad).length}</span>
        </div>
        <DownloadButtons period={period} filter={filter} type="zerosquad" extraParams={`&mode=${pctMode}&excludeDormant=${excludeDormant}&includeZeroSales=${includeZeroSales}${catFilter.length?`&categories=${catFilter.join(',')}`:''}`} />
      </div>
      <div style={{display:'flex',gap:4,marginBottom:8,flexWrap:'wrap',alignItems:'center'}}>
        {[{k:'all',l:'All'},{k:'staff',l:'Staff Only'},{k:'supervisor',l:'Supervisors'}].map(r=>(
          <button key={r.k} onClick={()=>setRoleFilter(r.k)} style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${roleFilter===r.k?C.blue:C.gray200}`,background:roleFilter===r.k?'#EFF6FF':C.white,color:roleFilter===r.k?C.blue:C.gray600,fontSize:11,fontWeight:600,cursor:'pointer'}}>{r.l}</button>
        ))}
        <span style={{color:C.gray200}}>|</span>
        <div style={{display:'flex',gap:2,background:C.gray100,borderRadius:8,padding:2}}>
          <PctModeToggle pctMode={pctMode} setPctMode={setPctMode} period={period} />
        </div>
      </div>
      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
        <span style={{fontSize:11,fontWeight:700,color:C.gray600}}>Categories:</span>
        {CAT_KEYS.map(k=>(
          <button key={k} onClick={()=>toggleCat(k)} style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${catFilter.includes(k)?C.blue:C.gray200}`,background:catFilter.includes(k)?'#EFF6FF':C.white,color:catFilter.includes(k)?C.blue:C.gray600,fontSize:11,fontWeight:600,cursor:'pointer'}}>{CAT_LABELS[k]}</button>
        ))}
        {catFilter.length>0 && <button onClick={()=>setCatFilter([])} style={{padding:'4px 10px',borderRadius:6,border:'none',background:'none',color:C.danger,fontSize:11,fontWeight:700,cursor:'pointer'}}>✕ Clear ({catFilter.length})</button>}
      </div>
      {showSettings&&(
        <div style={{background:C.gray50,border:`1px solid ${C.gray200}`,borderRadius:12,padding:14,marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:8}}>Zero Squad Thresholds (% of target)</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:6}}>
            {Object.entries(localThr).map(([k,v])=>(
              <div key={k} style={{display:'flex',alignItems:'center',gap:6}}>
                <label style={{fontSize:12,color:C.gray600,flex:1}}>{k.replace(/_/g,' ')}</label>
                <input type="number" value={v} onChange={e=>setLocalThr(t=>({...t,[k]:parseFloat(e.target.value)||0}))} style={{width:55,padding:'4px 6px',borderRadius:6,border:`1px solid ${C.gray200}`,fontSize:12,textAlign:'center'}} />
                <span style={{fontSize:11,color:C.gray400}}>%</span>
              </div>
            ))}
          </div>
          <button onClick={async()=>{await onUpdateThresholds(localThr);setShowSettings(false);}} style={{marginTop:8,padding:'5px 14px',borderRadius:8,border:'none',background:C.blue,color:C.white,fontWeight:700,fontSize:12,cursor:'pointer'}}>Save</button>
        </div>
      )}
      {list.length===0?<div style={{textAlign:'center',padding:40,color:C.gray400}}>{showOnly?'🎉 No underperformers!':'No data.'}</div>: catFilter.length>0 ? (
        // ── Expanded per-category detail view — selected categories only ──
        <ScrollableTableWrapper>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:C.gray900}}>
                <th rowSpan={2} style={{padding:'8px 10px',color:C.white,fontWeight:700,fontSize:11,whiteSpace:'nowrap'}}>Agent</th>
                <th rowSpan={2} style={{padding:'8px 10px',color:C.white,fontWeight:700,fontSize:11,whiteSpace:'nowrap'}}>Store</th>
                <th rowSpan={2} style={{padding:'8px 10px',color:C.white,fontWeight:700,fontSize:11,whiteSpace:'nowrap'}}>TL</th>
                {catFilter.map(k=>(
                  <th key={k} colSpan={8} style={{padding:'6px 10px',color:C.white,fontWeight:800,fontSize:12,textAlign:'center',borderLeft:`2px solid ${C.gray600}`}}>{CAT_LABELS[k]}</th>
                ))}
              </tr>
              <tr style={{background:C.gray800}}>
                {catFilter.map(k=>(
                  ['Target','MTD','MTD Tgt','Gap','CRR','RRR','Est.Close','%MTD','Rating'].slice(0,8).map((h,i)=>(
                    <th key={k+h} style={{padding:'5px 6px',color:C.white,fontWeight:600,fontSize:10,whiteSpace:'nowrap',borderLeft:i===0?`2px solid ${C.gray600}`:'none'}}>{h}</th>
                  ))
                ))}
              </tr>
            </thead>
            <tbody>{list.map(agent=>(
              <tr key={`${agent.storeCode}-${agent.agentName}`} style={{background:agent.isZeroSquad?'#FEF2F2':C.white,borderBottom:`2px solid ${C.gray200}`}}>
                <td style={{padding:'8px 10px',fontWeight:700,whiteSpace:'nowrap'}}>{agent.agentName}</td>
                <td style={{padding:'8px',textAlign:'center',color:C.gray600}}>{agent.storeCode.replace('DIGIWIRE','DW')}</td>
                <td style={{padding:'8px',textAlign:'center',color:C.gray600}}>{agent.teamLead}</td>
                {catFilter.map(k=>{
                  const cat = agent.categories.find(c=>c.key===k);
                  if(!cat) return <React.Fragment key={k}><td colSpan={8} style={{padding:'8px',textAlign:'center',color:C.gray400,borderLeft:`2px solid ${C.gray200}`}}>—</td></React.Fragment>;
                  const pct = pctMode==='mtd'?(cat.pctOfMtdTarget??0):cat.pctOfTarget;
                  const g=gapA(cat.gap);
                  const catRating = getCatRating(cat.pctOfMtdTarget ?? 0);
                  const rc = RS[catRating]||{bg:C.gray100,fg:C.gray600};
                  const isFlagged = agent.zeroSquadFlags?.includes(k);
                  return (
                    <React.Fragment key={k}>
                      <td style={{padding:'6px',textAlign:'center',borderLeft:`2px solid ${C.gray200}`,background:isFlagged?'#FEF2F2':'transparent'}}>{fmt(cat.target)}</td>
                      <td style={{padding:'6px',textAlign:'center',fontWeight:700,background:isFlagged?'#FEF2F2':'transparent'}}>{fmt(cat.mtd)}</td>
                      <td style={{padding:'6px',textAlign:'center',color:C.gray600,background:isFlagged?'#FEF2F2':'transparent'}}>{fmt(cat.mtdTarget)}</td>
                      <td style={{padding:'6px',textAlign:'center',fontWeight:700,color:g.c,background:isFlagged?'#FEF2F2':'transparent'}}>{g.i} {cat.gap>=0?'+':''}{fmt(cat.gap)}</td>
                      <td style={{padding:'6px',textAlign:'center',color:C.warning,background:isFlagged?'#FEF2F2':'transparent'}}>{fmt(cat.crr)}</td>
                      <td style={{padding:'6px',textAlign:'center',color:C.blue,background:isFlagged?'#FEF2F2':'transparent'}}>{fmt(cat.rrr)}</td>
                      <td style={{padding:'6px',textAlign:'center',background:isFlagged?'#FEF2F2':'transparent'}}>{fmt(cat.estClosing)}</td>
                      <td style={{padding:'6px',textAlign:'center',fontWeight:800,color:pct>=100?C.success:pct>=80?C.warning:C.danger,background:isFlagged?'#FEF2F2':'transparent'}}>{Math.round(pct)}%</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}</tbody>
          </table>
        </ScrollableTableWrapper>
      ) : (
        <ScrollableTableWrapper>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:C.gray800}}>
              {[{k:'agentName',l:'Agent'},{k:'storeCode',l:'Store'},{k:'teamLead',l:'TL'},...(list[0]?.categories||[]).map(c=>({k:c.key,l:c.label}))].map(h=>(
                <th key={h.k} onClick={()=>toggle(h.k)} style={{padding:'8px 10px',color:C.white,fontWeight:700,fontSize:11,textAlign:'center',whiteSpace:'nowrap',cursor:'pointer',userSelect:'none'}}>{h.l}{arrow(h.k)}</th>
              ))}
            </tr></thead>
            <tbody>{list.map(agent=>{
              return(
              <tr key={`${agent.storeCode}-${agent.agentName}`} style={{background:agent.isZeroSquad?'#FEF2F2':C.white,borderBottom:`2px solid ${C.gray200}`}}>
                <td style={{padding:'8px 10px',fontWeight:700,fontSize:14,whiteSpace:'nowrap'}}>{agent.agentName}</td>
                <td style={{padding:'8px',textAlign:'center',fontSize:12,color:C.gray600}}>{agent.storeCode.replace('DIGIWIRE','DW')}</td>
                <td style={{padding:'8px',textAlign:'center',fontSize:12,color:C.gray600}}>{agent.teamLead}</td>
                {agent.categories.map(cat=>{
                  const ref = pctMode==='mtd'?cat.mtdTarget:cat.target;
                  const pct = pctMode==='mtd'?(cat.pctOfMtdTarget??0):cat.pctOfTarget;
                  const fl=ref>0&&pct<(thresholds[cat.key]||60);
                  const g=gapA(cat.gap);
                  return<td key={cat.key} style={{padding:'6px 8px',textAlign:'center',background:fl?'#FECACA':'transparent'}}>
                    <div style={{fontWeight:700}}>{fmt(cat.mtd)}<span style={{color:C.gray400,fontWeight:400}}>/{fmt(ref)}</span></div>
                    <div style={{fontSize:11,color:g.c,fontWeight:700}}>{g.i} {cat.gap>=0?'+':''}{fmt(cat.gap)}</div>
                  </td>;
                })}
              </tr>
            );})}</tbody>
          </table>
        </ScrollableTableWrapper>
      )}
    </div>
  );
}

// ── POWER RANKINGS ───────────────────────────────────────────────────────────
function normalizeNameForMatch(s) {
  return (s || '').toLowerCase().trim().replace(/\s+/g, ' ');
}

// Sales data identifies people by name (agentName), not by a users.id — so
// logging a note from here first needs to resolve which login account
// that name actually belongs to. Uses the same appraisals.access-scoped
// employee list Team Notes already relies on elsewhere, matched by
// normalized name rather than assuming an exact string match.
export function AddPerformanceNoteModal({ agentName, storeCode, onClose }) {
  const [matchState, setMatchState] = useState('loading'); // loading | matched | not_found
  const [matchedUser, setMatchedUser] = useState(null);
  const [noteType, setNoteType] = useState('coaching');
  const [noteText, setNoteText] = useState('');
  const [tagLabel, setTagLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/appraisals/employees').then(r => {
      const target = normalizeNameForMatch(agentName);
      const match = r.data.find(u => normalizeNameForMatch(u.linked_staff_name || u.full_name) === target || normalizeNameForMatch(u.full_name) === target);
      if (match) { setMatchedUser(match); setMatchState('matched'); }
      else setMatchState('not_found');
    }).catch(() => setMatchState('not_found'));
  }, [agentName]);

  const submit = async () => {
    if (!matchedUser || !noteText.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/team-notes', {
        subject_user_id: matchedUser.id, type: noteType, note: noteText.trim(), tag_label: tagLabel.trim() || null,
      });
      toast.success('Note added.');
      onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to add note.'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: C.white, borderRadius: 16, padding: 24, width: 420, maxWidth: '92%' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 800, color: C.gray900, margin: '0 0 4px' }}>Note / Kudos — {agentName}</h2>

        {matchState === 'loading' && <p style={{ fontSize: 12.5, color: C.gray400 }}>Looking up their account…</p>}
        {matchState === 'not_found' && (
          <p style={{ fontSize: 12.5, color: C.danger, marginBottom: 16 }}>
            No linked account found for "{agentName}" — check their login is linked to this name under User Management before logging a note here.
          </p>
        )}
        {matchState === 'matched' && (
          <>
            <p style={{ fontSize: 12.5, color: C.gray400, marginTop: 0, marginBottom: 14 }}>
              This shows up automatically inside whichever future appraisal covers today's date for {matchedUser.full_name}.
            </p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <button onClick={() => setNoteType('coaching')} style={{ flex: 1, padding: '6px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${noteType === 'coaching' ? C.blue : C.gray200}`, background: noteType === 'coaching' ? '#EFF6FF' : C.white, color: noteType === 'coaching' ? C.blue : C.gray600 }}>📝 Coaching</button>
              <button onClick={() => setNoteType('kudos')} style={{ flex: 1, padding: '6px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${noteType === 'kudos' ? '#16A34A' : C.gray200}`, background: noteType === 'kudos' ? '#F0FDF4' : C.white, color: noteType === 'kudos' ? '#16A34A' : C.gray600 }}>⭐ Kudos</button>
            </div>
            <textarea placeholder="What happened?" value={noteText} onChange={e => setNoteText(e.target.value)} rows={3}
              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.gray200}`, fontSize: 12, fontFamily: 'inherit', marginBottom: 6, boxSizing: 'border-box' }} />
            <select value={tagLabel} onChange={e => setTagLabel(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.gray200}`, fontSize: 12, marginBottom: 16, boxSizing: 'border-box' }}>
              <option value="">No tag — won't link to a specific item</option>
              {NOTE_TAG_OPTIONS.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.options.map(o => <option key={o} value={o}>{o}</option>)}
                </optgroup>
              ))}
            </select>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, background: C.white, color: C.gray700, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {matchState === 'matched' ? 'Cancel' : 'Close'}
          </button>
          {matchState === 'matched' && (
            <button onClick={submit} disabled={saving || !noteText.trim()} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: C.blue, color: C.white, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PowerRankingsTab({agents,period,filter,thresholdMode,bands,excludeDormant,includeZeroSales,canAddNote}) {
  const [roleFilter,setRoleFilter]=useState('all');
  const [pctMode,setPctMode]=useState(thresholdMode||'target');
  useEffect(() => { if (isPastPeriod(period)) setPctMode('target'); }, [period]);
  const [expandedKey,setExpandedKey]=useState(null);
  const [noteFor,setNoteFor]=useState(null);

  const getAgentPct = (a) => {
    const cats = a.categories.filter(c => (pctMode==='mtd'?c.mtdTarget:c.target) > 0);
    if(cats.length===0) return 0;
    return cats.reduce((s,c)=>s+(pctMode==='mtd'?(c.pctOfMtdTarget||0):c.pctOfTarget),0)/cats.length;
  };
  const getRating=(pct)=>{const s=[...(bands||[])].sort((a,b)=>b.min_pct-a.min_pct);for(const b of s){const max=b.max_pct!=null?b.max_pct:Infinity;if(pct>=b.min_pct&&pct<=max)return b.label;}return s.length?s[s.length-1].label:'Unrated';};

  const ranked = useMemo(()=>{
    let filtered = agents;
    if(roleFilter==='staff') filtered = filtered.filter(a=>a.role!=='Supervisor');
    else if(roleFilter==='supervisor') filtered = filtered.filter(a=>a.role==='Supervisor');
    return [...filtered].map(a=>({...a, displayPct: getAgentPct(a)})).sort((a,b)=>b.displayPct-a.displayPct);
  },[agents,roleFilter,pctMode]);
  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:10,flexWrap:'wrap',gap:6,alignItems:'center'}}>
        <div style={{display:'flex',gap:4}}>
          {[{k:'all',l:'All Staff'},{k:'staff',l:'CSR / Asst Sup Only'},{k:'supervisor',l:'Supervisors Only'}].map(r=>(
            <button key={r.k} onClick={()=>setRoleFilter(r.k)} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${roleFilter===r.k?C.blue:C.gray200}`,background:roleFilter===r.k?'#EFF6FF':C.white,color:roleFilter===r.k?C.blue:C.gray600,fontSize:12,fontWeight:600,cursor:'pointer'}}>{r.l}</button>
          ))}
          <span style={{color:C.gray200}}>|</span>
          <div style={{display:'flex',gap:2,background:C.gray100,borderRadius:8,padding:2}}>
            <PctModeToggle pctMode={pctMode} setPctMode={setPctMode} period={period} />
          </div>
        </div>
        <DownloadButtons period={period} filter={filter} type="rankings" extraParams={`&mode=${pctMode}&excludeDormant=${excludeDormant}&includeZeroSales=${includeZeroSales}`}/>
      </div>
      {ranked.length===0?<div style={{textAlign:'center',padding:40,color:C.gray400}}>No data.</div>:(
        <div style={{display:'grid',gap:8}}>
          {ranked.map((a,idx)=>{
            const medal=idx===0?'🥇':idx===1?'🥈':idx===2?'🥉':`#${idx+1}`;
            const pc=a.displayPct>=100?C.success:a.displayPct>=80?C.warning:C.danger;
            const rating = getRating(a.displayPct);
            const rowKey = `${a.storeCode}-${a.agentName}`;
            const isExpanded = expandedKey === rowKey;
            return<div key={rowKey}>
              <div onClick={()=>setExpandedKey(isExpanded?null:rowKey)} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:idx<3?'#ECFDF5':C.white,borderRadius:isExpanded?'12px 12px 0 0':12,border:`1px solid ${idx<3?'#A7F3D0':C.gray200}`,borderLeft:`5px solid ${pc}`,cursor:'pointer'}}>
                <span style={{fontSize:idx<3?28:16,fontWeight:900,minWidth:40,textAlign:'center',color:idx<3?pc:C.gray400}}>{medal}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:15,fontWeight:700,color:C.gray900}}>{a.agentName}</div>
                  <div style={{fontSize:12,color:C.gray400}}>{a.storeCode.replace('DIGIWIRE','DW')} · {a.teamLead}</div>
                </div>
                <Badge rating={rating}/>
                <div style={{textAlign:'right',minWidth:70}}>
                  <div style={{fontSize:22,fontWeight:900,color:pc}}>{Math.round(a.displayPct)}%</div>
                </div>
                {canAddNote && (
                  <button onClick={e=>{e.stopPropagation();setNoteFor({name:a.agentName,storeCode:a.storeCode});}}
                    style={{fontSize:11,fontWeight:700,color:C.blue,background:'#EFF6FF',border:`1px solid ${C.blue}`,borderRadius:6,padding:'3px 9px',cursor:'pointer',whiteSpace:'nowrap'}}>
                    📝 Note
                  </button>
                )}
                <span style={{fontSize:12,color:C.gray400,marginLeft:4}}>{isExpanded?'▲':'▼'}</span>
              </div>
              {isExpanded && (
                <div style={{padding:'6px 16px 10px',background:C.gray50,border:`1px solid ${idx<3?'#A7F3D0':C.gray200}`,borderTop:'none',borderLeft:`5px solid ${pc}`,borderRadius:'0 0 12px 12px'}}>
                  {a.categories.filter(c=>(pctMode==='mtd'?c.mtdTarget:c.target)>0).map(c=>{
                    const cpct = pctMode==='mtd'?(c.pctOfMtdTarget||0):c.pctOfTarget;
                    const cpc = cpct>=100?C.success:cpct>=80?C.warning:C.danger;
                    return (
                      <div key={c.key} style={{display:'grid',gridTemplateColumns:'1fr 92px 46px',alignItems:'center',columnGap:10,padding:'4px 2px',fontSize:12.5}}>
                        <span style={{color:C.gray600,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{CAT_LABELS[c.key]||c.key}</span>
                        <span style={{color:C.gray900,fontWeight:700,textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(c.mtd)} / {fmt(pctMode==='mtd'?c.mtdTarget:c.target)}</span>
                        <span style={{fontWeight:800,color:cpc,textAlign:'right'}}>{Math.round(cpct)}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>;
          })}
        </div>
      )}
      {noteFor && <AddPerformanceNoteModal agentName={noteFor.name} storeCode={noteFor.storeCode} onClose={()=>setNoteFor(null)} />}
    </div>
  );
}

// ── CATEGORY RANKINGS ────────────────────────────────────────────────────────
function CategoryRankingsTab({agents,period,filter,bands,thresholdMode,excludeDormant,includeZeroSales}) {
  const [cat,setCat] = useState(CAT_KEYS[0]);
  const [roleFilter,setRoleFilter]=useState('all');
  const [pctMode,setPctMode]=useState(thresholdMode||'target');
  useEffect(() => { if (isPastPeriod(period)) setPctMode('target'); }, [period]);
  const {sortKey,sortDir,toggle,arrow}=useSortable('displayPct','desc');

  const ranked = useMemo(()=>{
    let filtered = agents;
    if(roleFilter==='staff') filtered = filtered.filter(a=>a.role!=='Supervisor');
    else if(roleFilter==='supervisor') filtered = filtered.filter(a=>a.role==='Supervisor');
    const mapped = [...filtered].map(a=>{
      const c = a.categories.find(x=>x.key===cat);
      if(!c) return null;
      const displayPct = pctMode==='mtd' ? (c.pctOfMtdTarget ?? 0) : c.pctOfTarget;
      const displayRef = pctMode==='mtd' ? c.mtdTarget : c.target;
      return {...a, catData: c, displayPct, displayRef};
    }).filter(a=>a && a.displayRef > 0);
    const getter = (item) => {
      if(sortKey==='agentName') return item.agentName;
      if(sortKey==='storeCode') return item.storeCode;
      if(sortKey==='displayPct') return item.displayPct;
      return item.catData[sortKey] ?? 0;
    };
    return sortBy(mapped, sortKey, sortDir, getter);
  },[agents,cat,sortKey,sortDir,roleFilter,pctMode,bands]);

  return (
    <div>
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        <span style={{fontSize:12,fontWeight:700,color:C.gray400}}>Category:</span>
        <select value={cat} onChange={e=>setCat(e.target.value)} style={{padding:'6px 10px',borderRadius:8,border:`1.5px solid ${C.gray200}`,fontSize:13,background:C.white}}>
          {CAT_KEYS.map(k=><option key={k} value={k}>{CAT_LABELS[k]}</option>)}
        </select>
        <span style={{color:C.gray200}}>|</span>
        {[{k:'all',l:'All'},{k:'staff',l:'Staff Only'},{k:'supervisor',l:'Supervisors'}].map(r=>(
          <button key={r.k} onClick={()=>setRoleFilter(r.k)} style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${roleFilter===r.k?C.blue:C.gray200}`,background:roleFilter===r.k?'#EFF6FF':C.white,color:roleFilter===r.k?C.blue:C.gray600,fontSize:11,fontWeight:600,cursor:'pointer'}}>{r.l}</button>
        ))}
        <span style={{color:C.gray200}}>|</span>
        <div style={{display:'flex',gap:2,background:C.gray100,borderRadius:8,padding:2}}>
          <PctModeToggle pctMode={pctMode} setPctMode={setPctMode} period={period} />
        </div>
        <div style={{flex:1}}/>
        <DownloadButtons period={period} filter={filter} type="categories" extraParams={`&category=${cat}&mode=${pctMode}&excludeDormant=${excludeDormant}&includeZeroSales=${includeZeroSales}`}/>
      </div>
      {ranked.length===0?<div style={{textAlign:'center',padding:40,color:C.gray400}}>No data.</div>:(
        <ScrollableTableWrapper>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:14}}>
            <thead><tr style={{background:C.gray800}}>
              {[{k:'rank',l:'Rank'},{k:'agentName',l:'Agent'},{k:'storeCode',l:'Store'},{k:'teamLead',l:'TL'},{k:'mtd',l:'MTD'},{k:'target',l:'Target'},{k:'mtdTarget',l:'MTD Tgt'},{k:'gap',l:'GAP'},{k:'x',l:''},{k:'rrr',l:'RRR'},{k:'crr',l:'CRR'},{k:'estClosing',l:'Est Close'},{k:'displayPct',l:pctMode==='mtd'?'% of MTD Tgt':'% of Target'}].map(h=>(
                <th key={h.k} onClick={()=>h.k!=='x'&&toggle(h.k)} style={{padding:'10px 12px',color:C.white,fontWeight:700,fontSize:12,textAlign:'center',whiteSpace:'nowrap',cursor:h.k==='x'?'default':'pointer',userSelect:'none'}}>{h.l}{h.k!=='x'?arrow(h.k):''}</th>
              ))}
            </tr></thead>
            <tbody>{ranked.map((a,idx)=>{
              const d=a.catData;
              const g=gapA(d.gap);
              const pc=a.displayPct>=100?C.success:a.displayPct>=80?C.warning:C.danger;
              return<tr key={`${a.storeCode}-${a.agentName}`} style={{background:idx<3?'#ECFDF5':idx%2===0?C.white:C.gray50,borderBottom:`2px solid ${C.gray200}`}}>
                <td style={{padding:'10px 12px',textAlign:'center',fontWeight:idx<3?900:600,fontSize:idx<3?18:14,color:idx<3?pc:C.gray400}}>{idx===0?'🥇':idx===1?'🥈':idx===2?'🥉':idx+1}</td>
                <td style={{padding:'10px 12px',fontWeight:700,fontSize:14}}>{a.agentName}</td>
                <td style={{padding:'10px',textAlign:'center',fontSize:12,color:C.gray600}}>{a.storeCode.replace('DIGIWIRE','DW')}</td>
                <td style={{padding:'10px',textAlign:'center',fontSize:12,color:C.gray600}}>{a.teamLead}</td>
                <td style={{padding:'10px 12px',textAlign:'center',fontWeight:800,fontSize:15}}>{fmt(d.mtd)}</td>
                <td style={{padding:'10px 12px',textAlign:'center',fontSize:14}}>{fmt(d.target)}</td>
                <td style={{padding:'10px 12px',textAlign:'center',color:C.gray400}}>{fmt(d.mtdTarget)}</td>
                <td style={{padding:'10px 12px',textAlign:'center',fontWeight:700,color:g.c}}>{d.gap>=0?'+':''}{fmt(d.gap)}</td>
                <td style={{textAlign:'center',fontSize:16}}><span style={{color:g.c,fontWeight:900}}>{g.i}</span></td>
                <td style={{padding:'10px 12px',textAlign:'center',color:C.warning,fontWeight:700,background:'#FFFBEB'}}>{fmt(d.rrr)}</td>
                <td style={{padding:'10px 12px',textAlign:'center'}}>{fmt(d.crr)}</td>
                <td style={{padding:'10px 12px',textAlign:'center',fontWeight:700}}>{fmt(d.estClosing)}</td>
                <td style={{padding:'10px 12px',textAlign:'center',fontWeight:800,fontSize:15,color:pc}}>{Math.round(a.displayPct)}%</td>
              </tr>;
            })}</tbody>
          </table>
        </ScrollableTableWrapper>
      )}
    </div>
  );
}

// ── SCORECARDS ───────────────────────────────────────────────────────────────
// Computes the Sales Score Card breakdown for one agent — the exact same
// shape used both on-screen and in the printed PDF, so they can never
// drift apart. Digicel+'s configured weight automatically folds into
// Devices when the agent's Digi+ target is 0 (store doesn't carry it).
function computeScorecard(agent, weights, bands, pctMode) {
  const getCat = (key) => agent.categories.find(c => c.key === key);
  const ref = (c) => (pctMode === 'mtd' ? c?.mtdTarget : c?.target) ?? 0;
  const actual = (c) => c?.mtd ?? 0;
  const pct = (c) => (pctMode === 'mtd' ? c?.pctOfMtdTarget : c?.pctOfTarget) ?? 0;

  const port = getCat('PORT'), sims = getCat('SIMS'), postpaid = getCat('POSTPAID'), digiplus = getCat('DIGI_PLUS'), devices = getCat('PHONES');
  const accessories = getCat('ACCESSORIES'), plans = getCat('PLANS');

  // "Prepaid" on the scorecard is Port + Prepaid combined, not Prepaid
  // alone -- Port stays its own separate category everywhere else (Set
  // Targets, Sales Summary), this combination is specific to how the
  // scorecard groups its core categories.
  const prepaidTarget = ref(port) + ref(sims);
  const prepaidActual = actual(port) + actual(sims);
  const prepaidPct = prepaidTarget > 0 ? (prepaidActual / prepaidTarget) * 100 : 0;

  const hasDigiPlus = ref(digiplus) > 0;
  const devicesWeight = weights.DEVICES + (hasDigiPlus ? 0 : (weights.DIGI_PLUS || 0));

  const core = [
    { key: 'PREPAID', label: 'Prepaid', weight: weights.PREPAID, target: prepaidTarget, actual: prepaidActual, pct: prepaidPct },
    { key: 'POSTPAID', label: 'Postpaid', weight: weights.POSTPAID, target: ref(postpaid), actual: actual(postpaid), pct: pct(postpaid) },
    ...(hasDigiPlus ? [{ key: 'DIGI_PLUS', label: 'Digicel+', weight: weights.DIGI_PLUS, target: ref(digiplus), actual: actual(digiplus), pct: pct(digiplus) }] : []),
    { key: 'DEVICES', label: 'Devices', weight: devicesWeight, target: ref(devices), actual: actual(devices), pct: pct(devices) },
  ].map(c => ({ ...c, weightedScore: (c.weight / 100) * c.pct }));

  const other = [
    { key: 'ACCESSORIES', label: 'Accessories', target: ref(accessories), actual: actual(accessories), pct: pct(accessories) },
    { key: 'PLANS', label: 'Plans', target: ref(plans), actual: actual(plans), pct: pct(plans) },
  ];

  const totalScore = core.reduce((s, c) => s + c.weightedScore, 0);
  const sortedBands = [...(bands || [])].sort((a, b) => b.min_pct - a.min_pct);
  let rating = 'Unrated';
  if (sortedBands.length > 0) {
    rating = null;
    for (const b of sortedBands) { const max = b.max_pct != null ? b.max_pct : Infinity; if (totalScore >= b.min_pct && totalScore <= max) { rating = b.label; break; } }
    if (rating == null) {
      // Bands are seeded contiguous on purpose, so this shouldn't normally
      // trigger -- see the matching comment on getRating in performance.js
      // for why the fallback here is the nearest band rather than always
      // the lowest, if a future manual edit ever reintroduces a gap.
      let closest = sortedBands[0], closestDist = Infinity;
      for (const b of sortedBands) {
        const max = b.max_pct != null ? b.max_pct : b.min_pct;
        const dist = totalScore < b.min_pct ? (b.min_pct - totalScore) : (totalScore - max);
        if (dist < closestDist) { closestDist = dist; closest = b; }
      }
      rating = closest.label;
    }
  }

  return { core, other, totalScore, rating, hasDigiPlus };
}

// For a Supervisor, builds a "virtual agent" whose categories reflect the
// store's FULL target — read directly from storeTargets (the raw,
// unprorated per-store figure, the same one Sales Summary uses) rather
// than summed from each roster member's own already-rounded prorated
// share. Summing already-rounded per-agent shares can drift a few units
// from the store's real total (round-each-then-sum vs sum-then-round-once
// — the same mismatch found between Scorecards and Sales Summary).
// Achieved (mtd) is unaffected by that issue and still sums each roster
// member's actual sales, exactly as before.
function buildStoreVirtualAgent(supervisorAgent, allAgents, storeTargets) {
  const storeAgents = allAgents.filter(a => a.storeCode === supervisorAgent.storeCode);
  const rawTargets = storeTargets?.[supervisorAgent.storeCode];
  const categories = CAT_KEYS.map(k => {
    const target = rawTargets ? (rawTargets[k] || 0)
      : storeAgents.reduce((s, a) => s + (a.categories.find(c => c.key === k)?.target || 0), 0);
    const mtd = storeAgents.reduce((s, a) => s + (a.categories.find(c => c.key === k)?.mtd || 0), 0);
    const mtdTarget = storeAgents.reduce((s, a) => s + (a.categories.find(c => c.key === k)?.mtdTarget || 0), 0);
    return {
      key: k, target, mtd, mtdTarget,
      pctOfTarget: target > 0 ? (mtd / target) * 100 : (mtd > 0 ? 999 : 0),
      pctOfMtdTarget: mtdTarget > 0 ? (mtd / mtdTarget) * 100 : (mtd > 0 ? 999 : 0),
    };
  });
  return { ...supervisorAgent, categories };
}

// Returns { individual, store } scorecards for a Supervisor, or just
// { individual } for anyone else — the store half is only meaningful for
// someone accountable for the whole store's numbers.
function computeSupervisorScorecards(agent, allAgents, weights, bands, pctMode, storeTargets) {
  const individual = computeScorecard(agent, weights, bands, pctMode);
  if (agent.role !== 'Supervisor') return { individual, store: null };
  const storeVirtual = buildStoreVirtualAgent(agent, allAgents, storeTargets);
  const store = computeScorecard(storeVirtual, weights, bands, pctMode);
  return { individual, store };
}

const RATING_DESCRIPTIONS = {
  'Exceeding Expectations': 'Overall target achievement exceeds the required threshold.',
  'Meets Expectations': 'Overall target achievement meets the required threshold.',
  'Improvement Needed': 'Overall target achievement is below the ideal threshold. Continued monitoring recommended.',
  'Unsatisfactory': 'Overall target achievement falls below the required threshold. Action plan required.',
};

function ScorecardBlock({ sc, sectionLabel }) {
  const ratingColor = sc.rating === 'Unsatisfactory' || sc.rating === 'Improvement Needed' ? '#DC2626' : '#059669';
  return (
    <div style={{ marginBottom: 28 }}>
      {sectionLabel && <div style={{ fontSize: 17, fontWeight: 900, color: '#0F172A', marginBottom: 10, background: C.gray100, padding: '6px 12px', borderRadius: 6 }}>{sectionLabel}</div>}

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', background: C.gray50, borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ textAlign: 'center', minWidth: 90 }}>
          <div style={{ fontSize: 36, fontWeight: 900, color: ratingColor }}>{sc.totalScore.toFixed(1)}%</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.gray400 }}>OVERALL SCORE</div>
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: ratingColor }}>Performance Rating: {sc.rating}</div>
          <div style={{ fontSize: 13, color: C.gray600 }}>{RATING_DESCRIPTIONS[sc.rating] || ''}</div>
        </div>
      </div>

      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6, borderLeft: '4px solid #0F172A', paddingLeft: 8 }}>CORE PERFORMANCE CATEGORIES</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18, fontSize: 14 }}>
        <thead><tr style={{ background: '#0F172A' }}>
          {['CATEGORY', 'WEIGHT', 'TARGET', 'ACTUAL', 'ACHIEVEMENT %', 'WEIGHTED SCORE'].map(h => (
            <th key={h} style={{ padding: '8px 10px', color: C.white, textAlign: h === 'CATEGORY' ? 'left' : 'right', fontSize: 12, fontWeight: 700 }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {sc.core.map((c, i) => (
            <tr key={c.key} style={{ background: i % 2 === 0 ? C.white : C.gray50, borderBottom: `1px solid ${C.gray200}` }}>
              <td style={{ padding: '8px 10px', fontWeight: 700 }}>{c.label.toUpperCase()}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{c.weight.toFixed(1)}%</td>
              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Math.round(c.target)}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Math.round(c.actual)}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{c.pct.toFixed(1)}%</td>
              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{c.weightedScore.toFixed(1)}%</td>
            </tr>
          ))}
          <tr style={{ background: C.gray100, fontWeight: 800 }}>
            <td colSpan={5} style={{ padding: '9px 10px' }}>TOTAL SCORE</td>
            <td style={{ padding: '9px 10px', textAlign: 'right', color: ratingColor }}>{sc.totalScore.toFixed(1)}%</td>
          </tr>
        </tbody>
      </table>

      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6, borderLeft: '4px solid #0F172A', paddingLeft: 8 }}>OTHER PERFORMANCE CATEGORIES</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 4, fontSize: 14 }}>
        <thead><tr style={{ background: '#0F172A' }}>
          {['CATEGORY', 'TARGET', 'ACTUAL', 'ACHIEVEMENT %'].map(h => (
            <th key={h} style={{ padding: '8px 10px', color: C.white, textAlign: h === 'CATEGORY' ? 'left' : 'right', fontSize: 12, fontWeight: 700 }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {sc.other.map((c, i) => (
            <tr key={c.key} style={{ background: i % 2 === 0 ? C.white : C.gray50, borderBottom: `1px solid ${C.gray200}` }}>
              <td style={{ padding: '8px 10px', fontWeight: 700 }}>{c.label.toUpperCase()}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Math.round(c.target)}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Math.round(c.actual)}</td>
              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{c.pct.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScoreCardPrintModal({ agent, allAgents, weights, bands, pctMode, period, onClose }) {
  const { individual, store } = computeSupervisorScorecards(agent, allAgents, weights, bands, pctMode);
  const sc = individual; // header banner (Digicel+ store label, etc.) still reflects the individual computation

  // Individual and store are printed as two separate documents, not one
  // combined PDF -- a Supervisor's store scorecard usually gets filed/used
  // differently than their personal one (e.g. one may go in their own
  // personnel file, the other tracks the store as a whole), so bundling
  // both into a single printout made that awkward. For a Supervisor, the
  // card type is chosen up front (before any comment is typed), since the
  // comment itself is specific to whichever document this run is for --
  // each gets its own, so picking Individual then Store back-to-back
  // doesn't overwrite what was typed for the other. Non-supervisors have
  // no store half at all, so they skip straight to the comment step,
  // exactly as before.
  const [step, setStep] = useState(store ? 'select' : 'comment'); // 'select' | 'comment' | 'preview'
  const [cardView, setCardView] = useState(null); // 'individual' | 'store' -- chosen in 'select', null until then
  const [individualComment, setIndividualComment] = useState('');
  const [storeComment, setStoreComment] = useState('');

  const effectiveView = cardView === 'store' && store ? 'store' : 'individual';
  const showing = effectiveView === 'store' ? store : individual;
  const showingLabel = effectiveView === 'store' ? 'Store Performance' : 'Individual Performance';
  const comment = effectiveView === 'store' ? storeComment : individualComment;
  const setComment = effectiveView === 'store' ? setStoreComment : setIndividualComment;

  if (step === 'select') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} className="no-print">
        <div style={{ background: C.white, borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Which scorecard?</div>
          <p style={{ fontSize: 12, color: C.gray400, marginBottom: 16 }}>{agent.agentName} is a Supervisor, so either can be printed -- as two separate documents, each with its own comments.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
            <button onClick={() => { setCardView('individual'); setStep('comment'); }} style={{ padding: '14px 16px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, background: C.gray50, color: C.gray900, fontWeight: 700, fontSize: 14, cursor: 'pointer', textAlign: 'left' }}>
              👤 Individual Performance<div style={{ fontWeight: 400, fontSize: 12, color: C.gray400, marginTop: 2 }}>{agent.agentName}'s own personal sales</div>
            </button>
            <button onClick={() => { setCardView('store'); setStep('comment'); }} style={{ padding: '14px 16px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, background: C.gray50, color: C.gray900, fontWeight: 700, fontSize: 14, cursor: 'pointer', textAlign: 'left' }}>
              🏪 Store Performance<div style={{ fontWeight: 400, fontSize: 12, color: C.gray400, marginTop: 2 }}>Their assigned store, as a whole</div>
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray600, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'comment') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} className="no-print">
        <div style={{ background: C.white, borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Manager Comments & Action Plan</div>
          <p style={{ fontSize: 12, color: C.gray400, marginBottom: 12 }}>For {agent.agentName}'s <strong>{showingLabel}</strong> scorecard — optional, printed at the bottom of this PDF.</p>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={5} placeholder="Enter review notes and performance comments..."
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 13, resize: 'vertical', marginBottom: 14, fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {store && <button onClick={() => setStep('select')} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray600, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginRight: 'auto' }}>← Back</button>}
            <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray600, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => setStep('preview')} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: C.blue, color: C.white, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Continue to Preview</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.white, zIndex: 300, overflowY: 'auto' }}>
      <div className="no-print" style={{ position: 'sticky', top: 0, background: C.gray900, padding: '10px 20px', paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>Print preview ({showingLabel}) — use your browser's print dialog to save as PDF</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {store && <button onClick={() => { setCardView(effectiveView === 'store' ? 'individual' : 'store'); setStep('comment'); }} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.gray600}`, background: 'transparent', color: C.white, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>🔁 Now do the {effectiveView === 'store' ? 'Individual' : 'Store'} one</button>}
          <button onClick={() => window.print()} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.blue, color: C.white, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>🖨️ Print / Save PDF</button>
          <button onClick={onClose} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.gray600}`, background: 'transparent', color: C.white, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>✕ Close</button>
        </div>
      </div>

      <div id="scorecard-print-area" style={{ maxWidth: 850, margin: '0 auto', padding: '40px 50px', fontFamily: 'Calibri, Arial, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '2px solid #0F172A', paddingBottom: 16 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#E4002B', letterSpacing: '-0.5px' }}>DIGI<span style={{ color: '#0F172A' }}>WIRELESS</span></div>
            <div style={{ fontSize: 9, color: C.gray400, fontWeight: 700, letterSpacing: '0.5px' }}>T&T'S LARGEST AUTHORISED DIGICEL DEALER</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: '#0F172A' }}>SALES SCORE CARD</div>
            <div style={{ fontSize: 12, color: C.gray400 }}>Monthly Performance Review • {showingLabel}{sc.hasDigiPlus ? ' • Digicel+ Store' : ' • Non-Digicel+ Store'}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, background: C.gray50, borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div><div style={{ fontSize: 11, fontWeight: 700, color: C.gray400 }}>EMPLOYEE NAME</div><div style={{ fontSize: 15, fontWeight: 700 }}>{agent.agentName}</div></div>
          <div><div style={{ fontSize: 11, fontWeight: 700, color: C.gray400 }}>POSITION</div><div style={{ fontSize: 15, fontWeight: 700 }}>{agent.role || '—'}</div></div>
          <div><div style={{ fontSize: 11, fontWeight: 700, color: C.gray400 }}>STORE LOCATION</div><div style={{ fontSize: 15, fontWeight: 700 }}>{agent.storeCode.replace('DIGIWIRE', 'DW')}</div></div>
          <div><div style={{ fontSize: 11, fontWeight: 700, color: C.gray400 }}>EVALUATION PERIOD</div><div style={{ fontSize: 15, fontWeight: 700 }}>{period}</div></div>
        </div>

        <ScorecardBlock sc={showing} sectionLabel={store ? (effectiveView === 'store' ? '🏪 STORE PERFORMANCE (assigned store, as a whole)' : '👤 INDIVIDUAL PERFORMANCE') : null} />

        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6, borderLeft: '4px solid #0F172A', paddingLeft: 8 }}>PERFORMANCE SCALE MATRIX</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18, fontSize: 13 }}>
          <tbody>
            {(() => {
              const sorted = [...(bands || [])].sort((a, b) => b.min_pct - a.min_pct);
              const rows = [];
              for (let i = 0; i < sorted.length; i += 2) rows.push(sorted.slice(i, i + 2));
              return rows.map((pair, ri) => (
                <tr key={ri} style={{ borderBottom: `1px solid ${C.gray200}` }}>
                  {pair.map((b, bi) => {
                    const rangeLabel = b.max_pct != null ? `${b.min_pct}% - ${b.max_pct}%` : `${b.min_pct}%+`;
                    const isBad = b.label === 'Unsatisfactory' || b.label === 'Improvement Needed';
                    return (
                      <React.Fragment key={b.label}>
                        <td style={{ padding: '7px 10px', fontWeight: 700, color: isBad ? '#DC2626' : C.gray800, width: '25%' }}>{rangeLabel}</td>
                        <td style={{ padding: '7px 10px', color: isBad ? '#DC2626' : C.gray800, fontWeight: isBad ? 700 : 400, width: '25%' }}>{b.label}</td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ));
            })()}
          </tbody>
        </table>

        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6, borderLeft: '4px solid #0F172A', paddingLeft: 8 }}>MANAGER COMMENTS & ACTION PLAN</div>
        <div style={{ minHeight: 70, border: `1px solid ${C.gray200}`, borderRadius: 8, padding: 12, fontSize: 14, color: comment ? C.gray900 : C.gray400, marginBottom: 30, whiteSpace: 'pre-wrap' }}>
          {comment || 'No comments entered.'}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, marginTop: 40 }}>
          <div style={{ borderTop: `1px solid ${C.gray600}`, paddingTop: 4, fontSize: 10, color: C.gray400 }}>Staff Signature</div>
          <div style={{ borderTop: `1px solid ${C.gray600}`, paddingTop: 4, fontSize: 10, color: C.gray400 }}>Sales Team Lead Signature</div>
          <div style={{ borderTop: `1px solid ${C.gray600}`, paddingTop: 4, fontSize: 10, color: C.gray400 }}>Date Discussed</div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body * { visibility: hidden; }
          #scorecard-print-area, #scorecard-print-area * { visibility: visible; }
          #scorecard-print-area { position: absolute; top: 0; left: 0; width: 100%; }
          @page { size: A4; margin: 15mm; }
        }
      `}</style>
    </div>
  );
}

function ScorecardsTab({agents,bands,onUpdateBands,isSM,period,filter,thresholdMode,excludeDormant,includeZeroSales,canAddNote,storeTargets,daysGone,workingDays}) {
  const [noteFor,setNoteFor]=useState(null);
  const [view,setView]=useState('stores');
  const [expandedStore,setExpandedStore]=useState(null);
  const [expandedTL,setExpandedTL]=useState(null);
  const [showBE,setShowBE]=useState(false);
  const [lb,setLB]=useState([]);
  const [pctMode,setPctMode]=useState('target'); // 'target' = full target (default -- matches what Set Targets always shows), 'mtd' = MTD-prorated target
  useEffect(() => { if (isPastPeriod(period)) setPctMode('target'); }, [period]);
  const [scWeights,setScWeights]=useState({PREPAID:5,POSTPAID:10,DIGI_PLUS:10,DEVICES:75});
  const [showWeightEditor,setShowWeightEditor]=useState(false);
  const [printAgent,setPrintAgent]=useState(null);
  useEffect(()=>{setLB([...(bands||[])].sort((a,b)=>b.min_pct-a.min_pct).map((b,i)=>({...b,_id:i})));},[bands]);
  useEffect(()=>{axios.get('/api/performance/scorecard-weights').then(r=>setScWeights(r.data)).catch(()=>{});},[]);

  const getRating=(pct)=>{const s=[...(bands||[])].sort((a,b)=>b.min_pct-a.min_pct);for(const b of s){const max=b.max_pct!=null?b.max_pct:Infinity;if(pct>=b.min_pct&&pct<=max)return b.label;}return s.length?s[s.length-1].label:'Unrated';};

  const getPct = (cats) => {
    const withTarget = cats.filter(c => (pctMode === 'mtd' ? c.mtdTarget : c.target) > 0);
    if (withTarget.length === 0) return 0;
    return withTarget.reduce((sum, c) => sum + (pctMode === 'mtd' ? (c.pctOfMtdTarget || 0) : c.pctOfTarget), 0) / withTarget.length;
  };

  // Target figures use the raw, unprorated per-store target (storeTargets) —
  // the same full number Sales Summary shows — rather than summing each
  // agent's individually reserved-adjusted share, per Avinash: a Team Lead
  // (and a store) is accountable for the FULL target across every store
  // they're responsible for, not just the portion left after a supervisor
  // reserve. Achieved (mtd) figures are unaffected and still summed from
  // agents as before.
  const storeCards=useMemo(()=>{
    const byS={};for(const a of agents)(byS[a.storeCode]=byS[a.storeCode]||[]).push(a);
    return Object.entries(byS).map(([code,ags])=>{
      const combinedCats = CAT_KEYS.map(k=>{
        const t=storeTargets?.[code]?.[k]||0;
        const m=ags.reduce((s,a)=>s+(a.categories.find(c=>c.key===k)?.mtd||0),0);
        const mt=workingDays>0?Math.round((t/workingDays)*daysGone):0;
        return {key:k, target:t, mtd:m, mtdTarget:mt, pctOfTarget: t>0?(m/t)*100:0, pctOfMtdTarget: mt>0?(m/mt)*100:0};
      });
      const avg = getPct(combinedCats);
      const best=CAT_KEYS.reduce((a,b)=>{const pa=combinedCats.find(c=>c.key===a);const pb=combinedCats.find(c=>c.key===b);return(pa?.pctOfTarget||0)>(pb?.pctOfTarget||0)?a:b;});
      const worst=CAT_KEYS.reduce((a,b)=>{const pa=combinedCats.find(c=>c.key===a);const pb=combinedCats.find(c=>c.key===b);return(pa?.pctOfTarget||0)<(pb?.pctOfTarget||0)?a:b;});
      return{code,tl:ags[0]?.teamLead||'',staff:ags.length,avg,best:CAT_LABELS[best],worst:CAT_LABELS[worst],cats:combinedCats};
    }).sort((a,b)=>b.avg-a.avg);
  },[agents,pctMode,storeTargets,daysGone,workingDays]);

  const tlCards=useMemo(()=>{
    const byT={};
    for(const a of agents) {
      const tl = a.teamLead || '';
      if(!tl) continue; // skip unassigned
      (byT[tl]=byT[tl]||[]).push(a);
    }
    return Object.entries(byT).map(([name,ags])=>{
      const storeCodes=[...new Set(ags.map(a=>a.storeCode))];
      const combinedCats = CAT_KEYS.map(k=>{
        const t=storeCodes.reduce((s,code)=>s+(storeTargets?.[code]?.[k]||0),0);
        const m=ags.reduce((s,a)=>s+(a.categories.find(c=>c.key===k)?.mtd||0),0);
        const mt=workingDays>0?Math.round((t/workingDays)*daysGone):0;
        return {key:k, target:t, mtd:m, mtdTarget:mt, pctOfTarget: t>0?(m/t)*100:0, pctOfMtdTarget: mt>0?(m/mt)*100:0};
      });
      const avg = getPct(combinedCats);
      return{name,stores:[...new Set(ags.map(a=>a.storeCode))].length,staff:ags.length,avg,cats:combinedCats};
    }).sort((a,b)=>b.avg-a.avg);
  },[agents,pctMode,storeTargets,daysGone,workingDays]);

  const tabs=[{key:'stores',label:'🏪 Stores'},{key:'tls',label:'👤 Team Leads'},{key:'agents',label:'👥 Individual'}];

  return (
    <div>
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        {tabs.map(t=><button key={t.key} onClick={()=>setView(t.key)} style={{padding:'7px 14px',borderRadius:8,border:'none',cursor:'pointer',fontSize:13,fontWeight:view===t.key?800:600,background:view===t.key?C.blue:'transparent',color:view===t.key?C.white:C.gray600}}>{t.label}</button>)}
        <span style={{color:C.gray200}}>|</span>
        <div style={{display:'flex',gap:2,background:C.gray100,borderRadius:8,padding:2}}>
          <PctModeToggle pctMode={pctMode} setPctMode={setPctMode} period={period} />
        </div>
        <div style={{flex:1}}/>
        {isSM && <button onClick={()=>{setShowBE(s=>!s);setShowWeightEditor(false);}} style={{padding:'5px 10px',borderRadius:6,border:`1px solid ${C.gray200}`,background:C.white,color:C.gray600,fontSize:11,fontWeight:600,cursor:'pointer'}}>⚙️ Rating Scale</button>}
        {isSM && <button onClick={()=>{setShowWeightEditor(s=>!s);setShowBE(false);}} style={{padding:'5px 10px',borderRadius:6,border:`1px solid ${C.gray200}`,background:C.white,color:C.gray600,fontSize:11,fontWeight:600,cursor:'pointer'}}>⚖️ Score Card Weights</button>}
        <DownloadButtons period={period} filter={filter} type="scorecards" extraParams={`&mode=${pctMode}&excludeDormant=${excludeDormant}&includeZeroSales=${includeZeroSales}`}/>
      </div>
      {showWeightEditor&&(
        <div style={{background:C.gray50,border:`1px solid ${C.gray200}`,borderRadius:12,padding:14,marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>Score Card Category Weights</div>
          <p style={{fontSize:11,color:C.gray400,marginBottom:8}}>Must add up to 100%. Digicel+'s weight automatically shifts into Devices for any store that doesn't carry Digi+.</p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:8,marginBottom:8}}>
            {[['PREPAID','Prepaid'],['POSTPAID','Postpaid'],['DIGI_PLUS','Digicel+'],['DEVICES','Devices']].map(([k,l])=>(
              <div key={k} style={{display:'flex',alignItems:'center',gap:6}}>
                <label style={{fontSize:12,color:C.gray600,flex:1}}>{l}</label>
                <input type="number" value={scWeights[k]??0} onChange={e=>setScWeights(w=>({...w,[k]:parseFloat(e.target.value)||0}))} style={{width:60,padding:'4px 6px',borderRadius:6,border:`1px solid ${C.gray200}`,fontSize:12,textAlign:'center'}} />
                <span style={{fontSize:11,color:C.gray400}}>%</span>
              </div>
            ))}
          </div>
          <div style={{fontSize:11,color:['PREPAID','POSTPAID','DIGI_PLUS','DEVICES'].reduce((s,k)=>s+(parseFloat(scWeights[k])||0),0)===100?C.success:C.danger,marginBottom:8,fontWeight:700}}>
            Total: {['PREPAID','POSTPAID','DIGI_PLUS','DEVICES'].reduce((s,k)=>s+(parseFloat(scWeights[k])||0),0).toFixed(1)}%
          </div>
          <button onClick={async()=>{try{await axios.put('/api/performance/scorecard-weights',scWeights);toast.success('Weights saved');setShowWeightEditor(false);}catch(e){toast.error(e.response?.data?.error||'Failed');}}} style={{padding:'5px 14px',borderRadius:8,border:'none',background:C.blue,color:C.white,fontWeight:700,fontSize:12,cursor:'pointer'}}>Save</button>
        </div>
      )}
      {showBE&&(
        <div style={{background:C.gray50,border:`1px solid ${C.gray200}`,borderRadius:12,padding:14,marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:8}}>Rating Bands (min% – max% → label)</div>
          <p style={{fontSize:11,color:C.gray400,marginBottom:8}}>Leave max blank for the top band ("and above"). Ranges don't have to be contiguous.</p>
          {lb.map((b)=>(
            <div key={b._id} style={{display:'flex',gap:8,alignItems:'center',marginBottom:4}}>
              <input type="number" value={b.min_pct} onChange={e=>{const v=e.target.value; setLB(prev=>prev.map(x=>x._id===b._id?{...x,min_pct:v}:x));}} style={{width:55,padding:'4px 6px',borderRadius:6,border:`1px solid ${C.gray200}`,fontSize:12,textAlign:'center'}}/>
              <span style={{fontSize:12,color:C.gray400}}>% –</span>
              <input type="number" value={b.max_pct??''} placeholder="∞" onChange={e=>{const v=e.target.value; setLB(prev=>prev.map(x=>x._id===b._id?{...x,max_pct:v===''?null:v}:x));}} style={{width:55,padding:'4px 6px',borderRadius:6,border:`1px solid ${C.gray200}`,fontSize:12,textAlign:'center'}}/>
              <span style={{fontSize:12,color:C.gray400}}>% →</span>
              <input value={b.label} onChange={e=>{const v=e.target.value; setLB(prev=>prev.map(x=>x._id===b._id?{...x,label:v}:x));}} style={{flex:1,padding:'4px 8px',borderRadius:6,border:`1px solid ${C.gray200}`,fontSize:12}}/>
            </div>
          ))}
          <button onClick={async()=>{const clean=lb.map(({_id,...rest})=>({...rest,min_pct:parseFloat(rest.min_pct)||0,max_pct:rest.max_pct===null||rest.max_pct===''?null:parseFloat(rest.max_pct)})).sort((a,b)=>b.min_pct-a.min_pct);await onUpdateBands(clean);setShowBE(false);}} style={{marginTop:8,padding:'5px 14px',borderRadius:8,border:'none',background:C.blue,color:C.white,fontWeight:700,fontSize:12,cursor:'pointer'}}>Save</button>
        </div>
      )}
      {view==='stores'&&<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:12}}>
        {storeCards.map(s=>{const r=getRating(s.avg);const rs=RS[r]||{bg:C.gray100,fg:C.gray600};const isOpen=expandedStore===s.code;return(
          <div key={s.code} style={{background:C.white,borderRadius:14,border:`1px solid ${C.gray200}`,borderLeft:`5px solid ${rs.fg}`,overflow:'hidden'}}>
            <div onClick={()=>setExpandedStore(isOpen?null:s.code)} style={{padding:'14px 16px',cursor:'pointer'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <div><div style={{fontSize:16,fontWeight:800,color:C.gray900}}>{s.code.replace('DIGIWIRE','DW')}</div><div style={{fontSize:11,color:C.gray400}}>{s.tl} · {s.staff} staff</div></div>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{textAlign:'right'}}><div style={{fontSize:24,fontWeight:900,color:rs.fg}}>{Math.round(s.avg)}%</div><Badge rating={r}/></div>
                  <span style={{fontSize:12,color:C.gray400}}>{isOpen?'▲':'▼'}</span>
                </div>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:C.gray600}}>
                <span>Best: <b style={{color:C.success}}>{s.best}</b></span>
                <span>Worst: <b style={{color:C.danger}}>{s.worst}</b></span>
              </div>
            </div>
            {isOpen&&(
              <div style={{padding:'6px 16px 14px',background:C.gray50,borderTop:`1px solid ${C.gray200}`}}>
                {s.cats.filter(c=>(pctMode==='mtd'?c.mtdTarget:c.target)>0).map(c=>{
                  const cpct=pctMode==='mtd'?(c.pctOfMtdTarget||0):c.pctOfTarget;
                  const cpc=cpct>=100?C.success:cpct>=80?C.warning:C.danger;
                  return(
                    <div key={c.key} style={{display:'grid',gridTemplateColumns:'1fr 110px 46px',alignItems:'center',columnGap:10,padding:'4px 2px',fontSize:12.5}}>
                      <span style={{color:C.gray600,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{CAT_LABELS[c.key]||c.key}</span>
                      <span style={{color:C.gray900,fontWeight:700,textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(c.mtd)} / {fmt(pctMode==='mtd'?c.mtdTarget:c.target)}</span>
                      <span style={{fontWeight:800,color:cpc,textAlign:'right'}}>{Math.round(cpct)}%</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );})}
      </div>}
      {view==='tls'&&<div style={{display:'grid',gap:10}}>
        {tlCards.map(tl=>{const r=getRating(tl.avg);const rs=RS[r]||{bg:C.gray100,fg:C.gray600};const isOpen=expandedTL===tl.name;return(
          <div key={tl.name} style={{background:C.white,borderRadius:14,border:`1px solid ${C.gray200}`,borderLeft:`5px solid ${rs.fg}`,overflow:'hidden'}}>
            <div onClick={()=>setExpandedTL(isOpen?null:tl.name)} style={{display:'flex',alignItems:'center',gap:14,padding:'14px 18px',cursor:'pointer'}}>
              <div style={{flex:1}}><div style={{fontSize:16,fontWeight:800}}>👤 {tl.name}</div><div style={{fontSize:12,color:C.gray400}}>{tl.stores} stores · {tl.staff} staff</div></div>
              <Badge rating={r}/><div style={{textAlign:'right',minWidth:70}}><div style={{fontSize:26,fontWeight:900,color:rs.fg}}>{Math.round(tl.avg)}%</div></div>
              <span style={{fontSize:12,color:C.gray400}}>{isOpen?'▲':'▼'}</span>
            </div>
            {isOpen&&(
              <div style={{padding:'6px 18px 14px',background:C.gray50,borderTop:`1px solid ${C.gray200}`}}>
                {tl.cats.filter(c=>(pctMode==='mtd'?c.mtdTarget:c.target)>0).map(c=>{
                  const cpct=pctMode==='mtd'?(c.pctOfMtdTarget||0):c.pctOfTarget;
                  const cpc=cpct>=100?C.success:cpct>=80?C.warning:C.danger;
                  return(
                    <div key={c.key} style={{display:'grid',gridTemplateColumns:'1fr 110px 46px',alignItems:'center',columnGap:10,padding:'4px 2px',fontSize:12.5}}>
                      <span style={{color:C.gray600,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{CAT_LABELS[c.key]||c.key}</span>
                      <span style={{color:C.gray900,fontWeight:700,textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(c.mtd)} / {fmt(pctMode==='mtd'?c.mtdTarget:c.target)}</span>
                      <span style={{fontWeight:800,color:cpc,textAlign:'right'}}>{Math.round(cpct)}%</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );})}
      </div>}
      {view==='agents'&&<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10}}>
        {[...agents].map(a=>({...a, _scs: computeSupervisorScorecards(a, agents, scWeights, bands, pctMode, storeTargets)})).sort((a,b)=>b._scs.individual.totalScore-a._scs.individual.totalScore).map(a=>{
          const sc = a._scs.individual;
          const storeSc = a._scs.store;
          const rs=RS[sc.rating]||{bg:C.gray100,fg:C.gray600};
          return(
          <div key={`${a.storeCode}-${a.agentName}`} style={{background:C.white,borderRadius:12,border:`1px solid ${C.gray200}`,padding:'12px 14px',borderLeft:`4px solid ${rs.fg}`}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
              <div><div style={{fontSize:14,fontWeight:700}}>{a.agentName}</div><div style={{fontSize:11,color:C.gray400}}>{a.storeCode.replace('DIGIWIRE','DW')} · {a.teamLead}</div></div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                {canAddNote && (
                  <button onClick={()=>setNoteFor({name:a.agentName,storeCode:a.storeCode})}
                    style={{fontSize:11,fontWeight:700,color:C.blue,background:'#EFF6FF',border:`1px solid ${C.blue}`,borderRadius:6,padding:'3px 9px',cursor:'pointer',whiteSpace:'nowrap'}}>
                    📝 Note
                  </button>
                )}
                <div style={{fontSize:20,fontWeight:900,color:rs.fg}}>{sc.totalScore.toFixed(1)}%</div>
              </div>
            </div>
            <Badge rating={sc.rating}/>
            <div style={{marginTop:8,fontSize:11}}>
              {storeSc && <div style={{fontSize:10,fontWeight:800,color:C.gray400,marginBottom:2,marginTop:4}}>👤 INDIVIDUAL</div>}
              {sc.core.map(c=>(
                <div key={c.key} style={{display:'flex',justifyContent:'space-between',padding:'2px 4px'}}>
                  <span style={{color:C.gray400}}>{c.label} <span style={{color:C.gray200}}>({c.weight.toFixed(0)}%)</span></span>
                  <span style={{fontWeight:700}}>{c.pct.toFixed(0)}% <span style={{color:C.gray400,fontWeight:400}}>→ {c.weightedScore.toFixed(1)}%</span></span>
                </div>
              ))}
              {storeSc && (
                <>
                  <div style={{display:'flex',justifyContent:'space-between',padding:'3px 4px',marginTop:4,borderTop:`1px solid ${C.gray100}`,paddingTop:6}}>
                    <span style={{fontSize:10,fontWeight:800,color:C.gray400}}>🏪 STORE (assigned)</span>
                    <span style={{fontSize:12,fontWeight:900,color:C.gray800}}>{storeSc.totalScore.toFixed(1)}%</span>
                  </div>
                  {storeSc.core.map(c=>(
                    <div key={c.key} style={{display:'flex',justifyContent:'space-between',padding:'2px 4px'}}>
                      <span style={{color:C.gray400}}>{c.label} <span style={{color:C.gray200}}>({c.weight.toFixed(0)}%)</span></span>
                      <span style={{fontWeight:700}}>{c.pct.toFixed(0)}% <span style={{color:C.gray400,fontWeight:400}}>→ {c.weightedScore.toFixed(1)}%</span></span>
                    </div>
                  ))}
                </>
              )}
            </div>
            <button onClick={()=>setPrintAgent(a)} style={{width:'100%',marginTop:10,padding:'7px',borderRadius:8,border:`1px solid ${C.gray200}`,background:C.gray50,color:C.gray800,fontWeight:700,fontSize:12,cursor:'pointer'}}>🖨️ Print Scorecard</button>
          </div>
        );})}
      </div>}
      {printAgent && <ScoreCardPrintModal agent={printAgent} allAgents={agents} weights={scWeights} bands={bands} pctMode={pctMode} period={period} onClose={()=>setPrintAgent(null)} />}
      {noteFor && <AddPerformanceNoteModal agentName={noteFor.name} storeCode={noteFor.storeCode} onClose={()=>setNoteFor(null)} />}
    </div>
  );
}

// ── SETTINGS TAB ─────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════
// PIP & COACHING TAB
// ═══════════════════════════════════════════════════════════════════════════
const PIP_STATUS_STYLE = {
  active:    { bg: '#DBEAFE', fg: '#1E40AF', icon: '📈', label: 'Active' },
  completed: { bg: '#D1FAE5', fg: '#065F46', icon: '✅', label: 'Completed' },
  failed:    { bg: '#FEE2E2', fg: '#991B1B', icon: '❌', label: 'Failed' },
  cancelled: { bg: C.gray100, fg: C.gray600, icon: '⊘', label: 'Cancelled' },
};

// A PIP's categories field mixes two kinds of entries: raw sales category
// codes (e.g. 'PHONES') from a weak sales item, and plain item names (e.g.
// 'Time management') from a weak behavioral item — both stored the same
// comma-joined way. Sales codes get mapped to their readable label;
// anything else is already human-readable text, so it's shown as-is.
const PIP_CATEGORY_LABELS = {
  PORT: 'Port', PRIME: 'Sim with Plan', SIM_ONLY: 'Sim Only', POSTPAID: 'Postpaid',
  DIGI_PLUS: 'Digicel+', ACCESSORIES: 'Accessories', PHONES: 'Handsets', PLANS: 'Prepaid Plans',
};
function pipCategoryLabels(categories) {
  if (!categories) return [];
  return categories.split(',').map(c => c.trim()).filter(Boolean).map(c => PIP_CATEGORY_LABELS[c] || c);
}

// ── PIP + Coaching Log Excel download ──
function PIPDownloadButtons({ pips }) {
  const dl = async (scope) => {
    const ids = scope === 'filtered' ? pips.map(p => p.id).join(',') : '';
    try {
      const res = await axios.get(`/api/performance/pip-report${ids ? `?ids=${ids}` : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.setAttribute('download', 'PIP_Coaching_Report.xlsx');
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(await blobErrorMessage(e, 'Download failed. Please try again.')); }
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <button onClick={() => dl('filtered')} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#059669,#047857)', color: C.white, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>📥 Download Filtered</button>
      <button onClick={() => dl('all')} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray600, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>📥 Download All</button>
    </div>
  );
}

function PIPCoachingTab({ period, isSM, data, filter }) {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [pips, setPips] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [roleFilter, setRoleFilter] = useState('all'); // all | staff | supervisor
  const [loading, setLoading] = useState(false);
  const [creatingFor, setCreatingFor] = useState(null); // candidate being turned into a PIP
  const [form, setForm] = useState({ target_period: '', improvement_target_pct: 80, notes: '' });
  const [notesModal, setNotesModal] = useState(null); // { agentName, storeCode, pipId }
  const [expandedPips, setExpandedPips] = useState({}); // pipId -> bool

  const load = async () => {
    if (!period) return;
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`/api/performance/pip-candidates/${period.split(',')[0]}`),
        axios.get(`/api/performance/pips${statusFilter ? `?status=${statusFilter}` : ''}`),
      ]);
      setCandidates(c.data.candidates || []);
      setPips(p.data || []);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load PIP data.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period, statusFilter]); // eslint-disable-line

  // Role lookup from the current period's performance data (Supervisor / Assistant Supervisor / Staff)
  const getRole = (agentName, storeCode) => {
    const a = data?.agents?.find(x => x.agentName === agentName && x.storeCode === storeCode);
    return a?.role || 'Staff';
  };
  const matchesRole = (agentName, storeCode) => {
    if (roleFilter === 'all') return true;
    const role = getRole(agentName, storeCode);
    return roleFilter === 'supervisor' ? role === 'Supervisor' : role !== 'Supervisor';
  };
  // TL / Store filter — reuses the same filter object driving the other tabs
  const matchesStoreFilter = (storeCode, agentName) => {
    if (!filter || filter.type === 'all') return true;
    if (filter.type === 'store') return storeCode === filter.value;
    if (filter.type === 'stores' && filter.stores) return filter.stores.includes(storeCode);
    if (filter.type === 'tl') {
      const a = data?.agents?.find(x => x.agentName === agentName && x.storeCode === storeCode);
      return a?.teamLead === filter.value;
    }
    return true;
  };

  const filteredCandidates = candidates.filter(c => matchesRole(c.agentName, c.storeCode) && matchesStoreFilter(c.storeCode, c.agentName));
  const filteredPips = pips.filter(p => matchesRole(p.agent_name, p.store_code) && matchesStoreFilter(p.store_code, p.agent_name));

  const startPip = (candidate) => {
    setCreatingFor(candidate);
    const [y, m] = period.split(',')[0].split('-').map(Number);
    const nextM = m + 2 > 12 ? m + 2 - 12 : m + 2;
    const nextY = m + 2 > 12 ? y + 1 : y;
    setForm({ target_period: `${nextY}-${String(nextM).padStart(2, '0')}`, improvement_target_pct: 80, notes: '' });
  };

  const submitPip = async () => {
    if (!creatingFor) return;
    await axios.post('/api/performance/pips', {
      agent_name: creatingFor.agentName, store_code: creatingFor.storeCode,
      start_period: period.split(',')[0], target_period: form.target_period,
      improvement_target_pct: form.improvement_target_pct, categories: [], notes: form.notes,
    });
    setCreatingFor(null);
    await load();
  };

  const updatePipStatus = async (id, status) => {
    await axios.put(`/api/performance/pips/${id}`, { status });
    await load();
  };

  const getCurrentPct = (agentName, storeCode) => {
    const a = data?.agents?.find(x => x.agentName === agentName && x.storeCode === storeCode);
    return a ? a.overallPct : null;
  };

  return (
    <div>
      {notesModal && <CoachingLogModal {...notesModal} onClose={() => setNotesModal(null)} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.gray400 }}>Role:</span>
        {[{k:'all',l:'All'},{k:'staff',l:'Staff Only'},{k:'supervisor',l:'Supervisors'}].map(r=>(
          <button key={r.k} onClick={()=>setRoleFilter(r.k)} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${roleFilter===r.k?C.blue:C.gray200}`,background:roleFilter===r.k?'#EFF6FF':C.white,color:roleFilter===r.k?C.blue:C.gray600,fontSize:12,fontWeight:600,cursor:'pointer'}}>{r.l}</button>
        ))}
        <div style={{ flex: 1 }} />
        <PIPDownloadButtons pips={filteredPips} />
      </div>

      {/* PIP Candidates */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: C.gray900, marginBottom: 4 }}>⚠️ Zero Squad Repeat Offenders</h3>
        <p style={{ fontSize: 12, color: C.gray400, marginBottom: 12 }}>Flagged in Zero Squad for 2+ consecutive months. Not yet on an active PIP.</p>
        {loading ? (
          <div style={{ fontSize: 13, color: C.gray400 }}>Loading...</div>
        ) : filteredCandidates.length === 0 ? (
          <div style={{ fontSize: 13, color: C.success, fontWeight: 600 }}>✓ No repeat offenders right now.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {filteredCandidates.map(c => {
              const ckey = `${c.storeCode}-${c.agentName}`;
              const isOpen = creatingFor && `${creatingFor.storeCode}-${creatingFor.agentName}` === ckey;
              return (
                <div key={ckey}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: isOpen ? '10px 10px 0 0' : 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.gray900 }}>{c.agentName}</div>
                      <div style={{ fontSize: 11, color: C.gray400 }}>{c.storeCode.replace('DIGIWIRE', 'DW')} · {c.teamLead}</div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 800, color: C.danger, background: '#FEE2E2', padding: '3px 10px', borderRadius: 8 }}>
                      {c.streakMonths} months flagged
                    </span>
                    <button onClick={() => isOpen ? setCreatingFor(null) : startPip(c)}
                      style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: isOpen ? C.gray200 : C.blue, color: isOpen ? C.gray600 : C.white, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                      {isOpen ? '▲ Close' : 'Start PIP'}
                    </button>
                  </div>

                  {isOpen && (
                    <div style={{ background: C.gray50, border: '1px solid #FECACA', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.gray800, marginBottom: 10 }}>New PIP for {creatingFor.agentName}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div>
                          <label style={{ fontSize: 11, color: C.gray400, display: 'block', marginBottom: 4 }}>Review by (target period)</label>
                          <input type="month" value={form.target_period} onChange={e => setForm(f => ({ ...f, target_period: e.target.value }))}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 13 }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: C.gray400, display: 'block', marginBottom: 4 }}>Target % to reach</label>
                          <input type="number" value={form.improvement_target_pct} onChange={e => setForm(f => ({ ...f, improvement_target_pct: parseFloat(e.target.value) || 0 }))}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 13 }} />
                        </div>
                      </div>
                      <label style={{ fontSize: 11, color: C.gray400, display: 'block', marginBottom: 4 }}>Notes / improvement plan</label>
                      <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 13, marginBottom: 10, fontFamily: 'inherit' }} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={submitPip} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: C.blue, color: C.white, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Create PIP</button>
                        <button onClick={() => setCreatingFor(null)} style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray600, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PIP List */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: C.gray900, margin: 0 }}>📋 Performance Improvement Plans</h3>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 12, background: C.white }}>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="">All</option>
          </select>
        </div>

        {filteredPips.length === 0 ? (
          <div style={{ fontSize: 13, color: C.gray400 }}>No PIPs in this status.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {filteredPips.map(pip => {
              const s = PIP_STATUS_STYLE[pip.status] || PIP_STATUS_STYLE.active;
              const currentPct = getCurrentPct(pip.agent_name, pip.store_code);
              const isExpanded = !!expandedPips[pip.id];
              return (
                <div key={pip.id} style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.gray200}`, borderLeft: `4px solid ${s.fg}`, overflow: 'hidden' }}>
                  <div onClick={() => setExpandedPips(p => ({ ...p, [pip.id]: !p[pip.id] }))}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', flexWrap: 'wrap', gap: 6, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: C.gray400 }}>{isExpanded ? '▼' : '▶'}</span>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: C.gray900 }}>{pip.agent_name}</div>
                        <div style={{ fontSize: 11, color: C.gray400 }}>{pip.store_code.replace('DIGIWIRE', 'DW')} · Started {pip.start_period} · Review by {pip.target_period}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {currentPct != null && <span style={{ fontSize: 13, fontWeight: 800, color: currentPct >= pip.improvement_target_pct ? C.success : C.danger }}>{Math.round(currentPct)}%</span>}
                      <span style={{ fontSize: 11, fontWeight: 800, color: s.fg, background: s.bg, padding: '3px 10px', borderRadius: 8 }}>{s.icon} {s.label}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '0 16px 16px 16px', borderTop: `1px solid ${C.gray100}` }}>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: C.gray600, margin: '10px 0 8px' }}>
                    <span>Target: <b>{pip.improvement_target_pct}%</b></span>
                    {currentPct != null && <span>Current: <b style={{ color: currentPct >= pip.improvement_target_pct ? C.success : C.danger }}>{Math.round(currentPct)}%</b></span>}
                  </div>
                  {pip.notes && <div style={{ fontSize: 12, color: C.gray600, background: C.gray50, padding: '8px 10px', borderRadius: 8, marginBottom: 10 }}>{pip.notes}</div>}
                  {pipCategoryLabels(pip.categories).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                      <span style={{ fontSize: 11, color: C.gray400, marginRight: 2 }}>Weak areas:</span>
                      {pipCategoryLabels(pip.categories).map((c, i) => (
                        <span key={i} style={{ fontSize: 11, fontWeight: 700, color: '#991B1B', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '2px 8px' }}>{c}</span>
                      ))}
                    </div>
                  )}
                  {pip.appraisal_id && (
                    <div onClick={() => navigate(`/appraisals?open=${pip.appraisal_id}`)}
                      style={{ fontSize: 11.5, fontWeight: 700, color: C.blue, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '6px 10px', marginBottom: 10, cursor: 'pointer' }}>
                      📋 Triggered by Appraisal #{pip.appraisal_id} (overall score {pip.appraisal_overall_score != null ? pip.appraisal_overall_score.toFixed(2) : '—'}) — view appraisal →
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => setNotesModal({ agentName: pip.agent_name, storeCode: pip.store_code, pipId: pip.id })}
                      style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.blue, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      📝 Coaching Log
                    </button>
                    {pip.status === 'active' && (
                      <>
                        <button onClick={() => updatePipStatus(pip.id, 'completed')}
                          style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: '#D1FAE5', color: '#065F46', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Mark Completed</button>
                        <button onClick={() => updatePipStatus(pip.id, 'failed')}
                          style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: '#FEE2E2', color: '#991B1B', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Mark Failed</button>
                        <button onClick={() => updatePipStatus(pip.id, 'cancelled')}
                          style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray400, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                      </>
                    )}
                  </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Coaching Log modal — view + add notes for one agent ──
function CoachingLogModal({ agentName, storeCode, pipId, onClose }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/performance/coaching?agentName=${encodeURIComponent(agentName)}&storeCode=${encodeURIComponent(storeCode)}`);
      setNotes(res.data || []);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load coaching notes.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const addNote = async () => {
    if (!newNote.trim()) return;
    await axios.post('/api/performance/coaching', { agent_name: agentName, store_code: storeCode, note: newNote.trim(), pip_id: pipId || null });
    setNewNote('');
    await load();
  };

  const deleteNote = async (id) => {
    await axios.delete(`/api/performance/coaching/${id}`);
    await load();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: 24, maxWidth: 520, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.gray900 }}>📝 Coaching Log — {agentName}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: C.gray400, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: C.gray400, marginBottom: 14 }}>{storeCode.replace('DIGIWIRE', 'DW')}</div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="e.g. Spoke to about port numbers, shadowed with TL..."
            onKeyDown={e => e.key === 'Enter' && addNote()}
            style={{ flex: 1, padding: '9px 12px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 13 }} />
          <button onClick={addNote} style={{ padding: '9px 16px', borderRadius: 10, border: 'none', background: C.blue, color: C.white, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Add</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ fontSize: 13, color: C.gray400 }}>Loading...</div>
          ) : notes.length === 0 ? (
            <div style={{ fontSize: 13, color: C.gray400, fontStyle: 'italic' }}>No notes yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {notes.map(n => (
                <div key={n.id} style={{ padding: '10px 12px', background: C.gray50, borderRadius: 10, border: `1px solid ${C.gray100}` }}>
                  <div style={{ fontSize: 13, color: C.gray800, marginBottom: 4 }}>{n.note}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: C.gray400 }}>{n.author_name || 'Unknown'} · {new Date(n.created_at).toLocaleDateString()}</span>
                    <button onClick={() => deleteNote(n.id)} style={{ fontSize: 11, color: C.danger, background: 'none', border: 'none', cursor: 'pointer' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsTab({thresholds,bands,onUpdateThresholds,onUpdateBands,thresholdMode,onUpdateMode,period}) {
  const [localThr,setLocalThr]=useState({});
  const [lb,setLB]=useState([]);
  const [saved,setSaved]=useState('');
  const [mode,setMode]=useState(thresholdMode||'target');
  useEffect(()=>{setLocalThr({...thresholds});},[thresholds]);
  useEffect(()=>{setLB([...(bands||[])].sort((a,b)=>b.min_pct-a.min_pct).map((b,i)=>({...b,_id:i})));},[bands]);

  return (
    <div style={{display:'grid',gap:20,maxWidth:700}}>
      <div style={{background:C.white,borderRadius:14,border:`1px solid ${C.gray200}`,padding:'18px 20px'}}>
        <h3 style={{fontSize:16,fontWeight:800,color:C.gray900,marginTop:0,marginBottom:12}}>🎯 Zero Squad Thresholds</h3>
        <p style={{fontSize:12,color:C.gray400,marginBottom:12}}>Agents performing below these % get flagged.</p>
        <div style={{display:'flex',gap:2,background:C.gray100,borderRadius:8,padding:2,marginBottom:14,width:'fit-content'}}>
          <button onClick={()=>{setMode('target');onUpdateMode('target');}} style={{padding:'6px 14px',borderRadius:6,border:'none',fontSize:12,fontWeight:700,cursor:'pointer',background:mode==='target'?C.white:'transparent',color:mode==='target'?C.blue:C.gray400}}>% of Full Target</button>
          <button onClick={()=>{setMode('mtd');onUpdateMode('mtd');}} style={{padding:'6px 14px',borderRadius:6,border:'none',fontSize:12,fontWeight:700,cursor:'pointer',background:mode==='mtd'?C.white:'transparent',color:mode==='mtd'?C.blue:C.gray400}}>% of MTD Target</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:8}}>
          {Object.entries(localThr).map(([k,v])=>(
            <div key={k} style={{display:'flex',alignItems:'center',gap:8}}>
              <label style={{fontSize:13,color:C.gray600,flex:1,fontWeight:600}}>{k.replace(/_/g,' ')}</label>
              <input type="number" value={v} onChange={e=>setLocalThr(t=>({...t,[k]:parseFloat(e.target.value)||0}))} style={{width:60,padding:'6px 8px',borderRadius:8,border:`1.5px solid ${C.gray200}`,fontSize:13,textAlign:'center'}} />
              <span style={{fontSize:12,color:C.gray400}}>%</span>
            </div>
          ))}
        </div>
        <button onClick={async()=>{await onUpdateThresholds(localThr);setSaved('thresholds');setTimeout(()=>setSaved(''),2000);}}
          style={{marginTop:14,padding:'8px 20px',borderRadius:8,border:'none',background:C.blue,color:C.white,fontWeight:700,fontSize:13,cursor:'pointer'}}>
          Save Thresholds
        </button>
        {saved==='thresholds'&&<span style={{marginLeft:8,color:C.success,fontWeight:700,fontSize:13}}>✓ Saved</span>}
      </div>

      <div style={{background:C.white,borderRadius:14,border:`1px solid ${C.gray200}`,padding:'18px 20px'}}>
        <h3 style={{fontSize:16,fontWeight:800,color:C.gray900,marginTop:0,marginBottom:12}}>📊 Rating Bands</h3>
        <p style={{fontSize:12,color:C.gray400,marginBottom:12}}>Performance ratings applied to scorecards and reports. Leave max blank for the top band ("and above").</p>
        {lb.map((b)=>{
          const rs=RS[b.label]||{bg:C.gray100,fg:C.gray600};
          return(
            <div key={b._id} style={{display:'flex',gap:8,alignItems:'center',marginBottom:6,padding:'6px 10px',borderRadius:8,background:rs.bg}}>
              <input type="number" value={b.min_pct} onChange={e=>{const v=e.target.value; setLB(prev=>prev.map(x=>x._id===b._id?{...x,min_pct:v}:x));}}
                style={{width:60,padding:'6px 8px',borderRadius:8,border:`1.5px solid ${C.gray200}`,fontSize:13,textAlign:'center'}} />
              <span style={{fontSize:13,color:rs.fg,fontWeight:700}}>% –</span>
              <input type="number" value={b.max_pct??''} placeholder="∞" onChange={e=>{const v=e.target.value; setLB(prev=>prev.map(x=>x._id===b._id?{...x,max_pct:v===''?null:v}:x));}}
                style={{width:60,padding:'6px 8px',borderRadius:8,border:`1.5px solid ${C.gray200}`,fontSize:13,textAlign:'center'}} />
              <span style={{fontSize:13,color:rs.fg,fontWeight:700}}>% →</span>
              <input value={b.label} onChange={e=>{const v=e.target.value; setLB(prev=>prev.map(x=>x._id===b._id?{...x,label:v}:x));}}
                style={{flex:1,padding:'6px 10px',borderRadius:8,border:`1.5px solid ${C.gray200}`,fontSize:13,fontWeight:700,color:rs.fg}} />
            </div>
          );
        })}
        <button onClick={async()=>{const clean=lb.map(({_id,...rest})=>({...rest,min_pct:parseFloat(rest.min_pct)||0,max_pct:rest.max_pct===null||rest.max_pct===''?null:parseFloat(rest.max_pct)})).sort((a,b)=>b.min_pct-a.min_pct);await onUpdateBands(clean);setSaved('bands');setTimeout(()=>setSaved(''),2000);}}
          style={{marginTop:14,padding:'8px 20px',borderRadius:8,border:'none',background:C.blue,color:C.white,fontWeight:700,fontSize:13,cursor:'pointer'}}>
          Save Rating Bands
        </button>
        {saved==='bands'&&<span style={{marginLeft:8,color:C.success,fontWeight:700,fontSize:13}}>✓ Saved</span>}
      </div>

      <ScoreCardWeightsSettings onSaved={()=>{setSaved('weights');setTimeout(()=>setSaved(''),2000);}} saved={saved==='weights'} />

      <NameReconciliationPanel period={period} />
    </div>
  );
}

function ScoreCardWeightsSettings({ onSaved, saved }) {
  const [weights, setWeights] = useState({ PREPAID: 5, POSTPAID: 10, DIGI_PLUS: 10, DEVICES: 75 });
  useEffect(() => { axios.get('/api/performance/scorecard-weights').then(r => setWeights(r.data)).catch(() => {}); }, []);
  const total = ['PREPAID', 'POSTPAID', 'DIGI_PLUS', 'DEVICES'].reduce((s, k) => s + (parseFloat(weights[k]) || 0), 0);

  return (
    <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.gray200}`, padding: '18px 20px' }}>
      <h3 style={{ fontSize: 16, fontWeight: 800, color: C.gray900, marginTop: 0, marginBottom: 12 }}>⚖️ Score Card Category Weights</h3>
      <p style={{ fontSize: 12, color: C.gray400, marginBottom: 12 }}>How much each category counts toward a Sales Score Card's overall score. Must add up to 100%. Digicel+'s weight automatically shifts into Devices for any store that doesn't carry Digi+.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8, marginBottom: 8 }}>
        {[['PREPAID', 'Prepaid'], ['POSTPAID', 'Postpaid'], ['DIGI_PLUS', 'Digicel+'], ['DEVICES', 'Devices']].map(([k, l]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, color: C.gray600, flex: 1, fontWeight: 600 }}>{l}</label>
            <input type="number" value={weights[k] ?? 0} onChange={e => setWeights(w => ({ ...w, [k]: parseFloat(e.target.value) || 0 }))} style={{ width: 60, padding: '6px 8px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 13, textAlign: 'center' }} />
            <span style={{ fontSize: 12, color: C.gray400 }}>%</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: total === 100 ? C.success : C.danger, marginBottom: 10 }}>Total: {total.toFixed(1)}%</div>
      <button onClick={async () => { try { await axios.put('/api/performance/scorecard-weights', weights); onSaved(); } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); } }}
        style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: C.blue, color: C.white, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
        Save Weights
      </button>
      {saved && <span style={{ marginLeft: 8, color: C.success, fontWeight: 700, fontSize: 13 }}>✓ Saved</span>}
    </div>
  );
}

// ── Name Reconciliation — link a sales-data spelling to a roster identity ──
function NameReconciliationPanel({ period }) {
  const [unmatched, setUnmatched] = useState([]);
  const [rosterNames, setRosterNames] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [picks, setPicks] = useState({}); // rawName -> chosen canonical name
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState('');

  const load = async () => {
    if (!period) return;
    setLoading(true);
    try {
      const [u, a] = await Promise.all([
        axios.get(`/api/performance/unmatched/${period}`),
        axios.get('/api/performance/aliases'),
      ]);
      setUnmatched(u.data.unmatched || []);
      setRosterNames(u.data.rosterNames || []);
      setAliases(a.data || []);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load reclassification data.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period]); // eslint-disable-line

  const linkPerson = async (rawName) => {
    const canonical = picks[rawName];
    if (!canonical) return;
    await axios.post('/api/performance/aliases', { alias_name: rawName, canonical_name: canonical });
    setSaved(rawName);
    setTimeout(() => setSaved(''), 2000);
    await load();
  };

  const removeAlias = async (id) => {
    await axios.delete(`/api/performance/aliases/${id}`);
    await load();
  };

  return (
    <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.gray200}`, padding: '18px 20px' }}>
      <h3 style={{ fontSize: 16, fontWeight: 800, color: C.gray900, marginTop: 0, marginBottom: 4 }}>🔗 Name Reconciliation</h3>
      <p style={{ fontSize: 12, color: C.gray400, marginBottom: 14, lineHeight: 1.5 }}>
        Sales data spellings that don't exactly match a roster name show up separately instead of rolling into that
        person's real numbers. Link them below — once linked, they'll always be treated as the same person.
      </p>

      {loading ? (
        <div style={{ fontSize: 13, color: C.gray400 }}>Loading...</div>
      ) : unmatched.length === 0 ? (
        <div style={{ fontSize: 13, color: C.success, fontWeight: 600 }}>✓ No unmatched names for this period.</div>
      ) : (
        <div style={{ display: 'grid', gap: 8, marginBottom: 18 }}>
          {unmatched.map(u => (
            <div key={u.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 160 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.gray900 }}>{u.name}</div>
                <div style={{ fontSize: 11, color: C.gray400 }}>{u.storeCode.replace('DIGIWIRE', 'DW')} · {u.totalSales} total sales</div>
              </div>
              <span style={{ fontSize: 12, color: C.gray400 }}>is the same person as</span>
              <select value={picks[u.name] || ''} onChange={e => setPicks(p => ({ ...p, [u.name]: e.target.value }))}
                style={{ flex: 1, minWidth: 180, padding: '6px 8px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 12 }}>
                <option value="">Select roster name...</option>
                {rosterNames.map(r => <option key={r.fullName} value={r.fullName}>{r.fullName} ({r.storeCode.replace('DIGIWIRE', 'DW')})</option>)}
              </select>
              <button onClick={() => linkPerson(u.name)} disabled={!picks[u.name]}
                style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: picks[u.name] ? C.blue : C.gray200, color: C.white, fontWeight: 700, fontSize: 12, cursor: picks[u.name] ? 'pointer' : 'not-allowed' }}>
                Link
              </button>
              {saved === u.name && <span style={{ color: C.success, fontWeight: 700, fontSize: 12 }}>✓ Linked</span>}
            </div>
          ))}
        </div>
      )}

      {aliases.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.gray800, marginBottom: 8 }}>Confirmed Links</div>
          <div style={{ display: 'grid', gap: 4 }}>
            {aliases.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: C.gray50, borderRadius: 8 }}>
                <span style={{ fontSize: 12, color: C.gray700 }}>
                  <b>{a.alias_name}</b> <span style={{ color: C.gray400 }}>→</span> <b>{a.canonical_name}</b>
                </span>
                <button onClick={() => removeAlias(a.id)} style={{ fontSize: 11, color: C.danger, background: 'none', border: 'none', cursor: 'pointer' }}>Unlink</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dormant Staff Prompt Modal ──────────────────────────────────────────────
function DormantStaffModal({agents, onInclude, onExclude, onCancel}) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:C.white,borderRadius:16,padding:24,maxWidth:480,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
        <div style={{fontSize:18,fontWeight:800,color:C.gray900,marginBottom:6}}>⚠️ Former Staff Found</div>
        <p style={{fontSize:13,color:C.gray600,marginBottom:14,lineHeight:1.5}}>
          The selected months include sales from {agents.length} staff member{agents.length>1?'s':''} who {agents.length>1?'are':'is'} no longer active (marked dormant). Include them in this report?
        </p>
        <div style={{maxHeight:180,overflowY:'auto',border:`1px solid ${C.gray200}`,borderRadius:10,padding:'8px 12px',marginBottom:18,background:C.gray50}}>
          {agents.map((a,i)=>(
            <div key={i} style={{fontSize:13,color:C.gray700,padding:'3px 0'}}>
              {a.agentName} <span style={{color:C.gray400}}>— {a.storeCode.replace('DIGIWIRE','DW')}</span>
            </div>
          ))}
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onCancel} style={{padding:'8px 16px',borderRadius:8,border:`1px solid ${C.gray200}`,background:C.white,color:C.gray600,fontWeight:600,fontSize:13,cursor:'pointer'}}>Cancel</button>
          <button onClick={onExclude} style={{padding:'8px 16px',borderRadius:8,border:'none',background:C.gray100,color:C.gray800,fontWeight:700,fontSize:13,cursor:'pointer'}}>Exclude Them</button>
          <button onClick={onInclude} style={{padding:'8px 16px',borderRadius:8,border:'none',background:C.blue,color:C.white,fontWeight:700,fontSize:13,cursor:'pointer'}}>Include Them</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function PerformancePage() {
  const { can } = useAuth();
  const [tab,setTabRaw]=useState('zerosquad');
  const [prevTab,setPrevTab]=useState('zerosquad');
  const setTab=(next)=>{ setPrevTab(tab==='settings'?prevTab:tab); setTabRaw(next); };
  const [periods,setPeriods]=useState([]);
  const [period,setPeriod]=useState('');
  const [data,setData]=useState(null);
  const [thresholds,setThresholds]=useState({});
  const [bands,setBands]=useState([]);
  const [loading,setLoading]=useState(false);
  const [userRole,setUserRole]=useState('');
  const [filter,setFilter]=useState({type:'all'});
  const [showMulti,setShowMulti]=useState(false);
  const [selectedPeriods,setSelectedPeriods]=useState([]);
  const [thresholdMode,setThresholdMode]=useState('mtd'); // 'target' or 'mtd' — defaults to MTD pace
  const [excludeDormant,setExcludeDormant]=useState(false);
  const [dormantModal,setDormantModal]=useState(null); // { agents: [...], pendingPeriod: '...' }
  const [includeZeroSales,setIncludeZeroSales]=useState(true);

  useEffect(()=>{
    axios.get('/api/sales/periods').then(r=>{setPeriods(r.data);if(r.data.length>0)setPeriod(r.data[0].period);}).catch(()=>{});
    try{const t=localStorage.getItem('dwpulse_token');if(t){setUserRole(JSON.parse(atob(t.split('.')[1])).role||'');}}catch{}
  },[]);

  const fetchData=useCallback(async()=>{
    if(!period)return;setLoading(true);
    try{
      const [p,t,b]=await Promise.all([
        axios.get(`/api/performance/data/${period}?excludeDormant=${excludeDormant}&includeZeroSales=${includeZeroSales}`),
        axios.get('/api/performance/thresholds'),
        axios.get('/api/performance/bands'),
      ]);
      setData({...p.data,period});setThresholds(t.data);setBands(b.data);
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  },[period,excludeDormant,includeZeroSales]);

  // When applying a multi-month selection, check for dormant/former staff first
  const applyMultiMonth = async () => {
    if (selectedPeriods.length === 0) return;
    if (selectedPeriods.length === 1) {
      // Single period — no need to check, dormant staff are only a concern across months
      setExcludeDormant(false);
      setPeriod(selectedPeriods[0]);
      setShowMulti(false);
      return;
    }
    try {
      const res = await axios.get(`/api/performance/dormant-check?periods=${selectedPeriods.join(',')}`);
      if (res.data.dormantAgents && res.data.dormantAgents.length > 0) {
        setDormantModal({ agents: res.data.dormantAgents, pendingPeriod: selectedPeriods.join(',') });
      } else {
        setExcludeDormant(false);
        setPeriod(selectedPeriods.join(','));
        setShowMulti(false);
      }
    } catch (e) {
      // If the check fails, proceed without blocking
      setExcludeDormant(false);
      setPeriod(selectedPeriods.join(','));
      setShowMulti(false);
    }
  };

  useEffect(()=>{setFilter({type:'all'});fetchData();},[fetchData]);

  const isSM=userRole==='senior_manager'||userRole==='admin'||userRole==='super_admin';
  const filtered=useFiltered(data,filter);

  // Re-flag agents based on threshold mode (target vs MTD target)
  const flaggedFiltered = useMemo(() => {
    return filtered.map(a => {
      const zeroSquadFlags = [];
      for (const cat of a.categories) {
        const thr = thresholds[cat.key] || 60;
        const pct = thresholdMode === 'mtd' ? (cat.pctOfMtdTarget || 0) : cat.pctOfTarget;
        const ref = thresholdMode === 'mtd' ? cat.mtdTarget : cat.target;
        if (ref > 0 && pct < thr) zeroSquadFlags.push(cat.key);
      }
      return { ...a, zeroSquadFlags, isZeroSquad: zeroSquadFlags.length > 0 };
    });
  }, [filtered, thresholds, thresholdMode]);

  const zsCount=flaggedFiltered.filter(a=>a.isZeroSquad).length;

  const TABS=[
    {key:'zerosquad',label:'🔴 Zero Squad',count:zsCount},
    {key:'rankings',label:'⚡ Power Rankings'},
    {key:'categories',label:'📊 Category Rankings'},
    {key:'scorecards',label:'📋 Scorecards'},
    {key:'pip',label:'📈 PIP & Coaching'},
    ...(isSM?[{key:'settings',label:'⚙️ Settings'}]:[]),
  ];

  return (
    <div>
      {dormantModal && (
        <DormantStaffModal
          agents={dormantModal.agents}
          onInclude={()=>{setExcludeDormant(false);setPeriod(dormantModal.pendingPeriod);setShowMulti(false);setDormantModal(null);}}
          onExclude={()=>{setExcludeDormant(true);setPeriod(dormantModal.pendingPeriod);setShowMulti(false);setDormantModal(null);}}
          onCancel={()=>setDormantModal(null)}
        />
      )}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:8}}>
        <h1 style={{fontSize:22,fontWeight:800,color:C.gray900,margin:0}}>Performance</h1>
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          <select value={period} onChange={e=>{setPeriod(e.target.value);setSelectedPeriods([e.target.value]);}} style={{padding:'7px 12px',borderRadius:8,border:`1.5px solid ${C.gray200}`,fontSize:13,background:C.white}}>
            {periods.map(p=><option key={p.period} value={p.period}>{p.period}</option>)}
          </select>
          <button onClick={()=>setShowMulti(s=>!s)} style={{padding:'5px 10px',borderRadius:6,border:`1px solid ${C.gray200}`,background:showMulti?'#EFF6FF':C.white,color:showMulti?C.blue:C.gray400,fontSize:11,fontWeight:600,cursor:'pointer'}}>📅 Multi-month</button>
          {excludeDormant && <span style={{fontSize:11,fontWeight:700,color:C.warning,background:'#FEF3C7',padding:'4px 10px',borderRadius:6}}>Former staff excluded</span>}
          <label style={{display:'flex',alignItems:'center',gap:4,fontSize:11,fontWeight:600,color:C.gray600,cursor:'pointer',padding:'4px 10px',borderRadius:6,border:`1px solid ${C.gray200}`}}>
            <input type="checkbox" checked={includeZeroSales} onChange={e=>setIncludeZeroSales(e.target.checked)} />
            Show staff with $0 sales
          </label>
        </div>
      </div>

      {showMulti&&(
        <div style={{display:'flex',gap:4,flexWrap:'wrap',alignItems:'center',marginBottom:12,padding:'8px 12px',background:C.gray50,borderRadius:10,border:`1px solid ${C.gray200}`}}>
          <span style={{fontSize:12,fontWeight:700,color:C.gray600}}>Select months:</span>
          {periods.map(p=>(
            <label key={p.period} style={{display:'flex',alignItems:'center',gap:3,fontSize:12,cursor:'pointer'}}>
              <input type="checkbox" checked={selectedPeriods.includes(p.period)} onChange={e=>{
                setSelectedPeriods(prev=>e.target.checked?[...prev,p.period].sort():prev.filter(x=>x!==p.period));
              }}/>
              {p.period}
            </label>
          ))}
          <button onClick={applyMultiMonth} style={{padding:'4px 12px',borderRadius:6,border:'none',background:C.blue,color:C.white,fontSize:11,fontWeight:700,cursor:'pointer'}}>Apply</button>
        </div>
      )}

      {!loading&&data&&<FilterBar data={data} filter={filter} setFilter={setFilter} />}

      <div style={{display:'flex',gap:4,marginBottom:16,borderBottom:`2px solid ${C.gray100}`,paddingBottom:8}}>
        {TABS.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)} style={{padding:'8px 16px',borderRadius:'8px 8px 0 0',border:'none',cursor:'pointer',fontSize:14,fontWeight:tab===t.key?800:600,background:tab===t.key?C.blue:'transparent',color:tab===t.key?C.white:C.gray600}}>
            {t.label}{t.count>0&&<span style={{marginLeft:5,padding:'1px 6px',borderRadius:10,background:tab===t.key?'rgba(255,255,255,0.3)':'#FEE2E2',color:tab===t.key?C.white:C.danger,fontSize:11,fontWeight:800}}>{t.count}</span>}
          </button>
        ))}
      </div>

      {loading&&<div style={{textAlign:'center',padding:40,color:C.gray400}}>Loading...</div>}
      {!loading&&tab==='zerosquad'&&<ZeroSquadTab agents={flaggedFiltered} thresholds={thresholds} onUpdateThresholds={async t=>{await axios.put('/api/performance/thresholds',t);await fetchData();}} isSM={isSM} period={period} filter={filter} thresholdMode={thresholdMode} excludeDormant={excludeDormant} includeZeroSales={includeZeroSales} bands={bands}/>}
      {!loading&&tab==='rankings'&&<PowerRankingsTab agents={flaggedFiltered} period={period} filter={filter} thresholdMode={thresholdMode} bands={bands} excludeDormant={excludeDormant} includeZeroSales={includeZeroSales} canAddNote={can('appraisals.edit')==='true'}/>}
      {!loading&&tab==='categories'&&<CategoryRankingsTab agents={flaggedFiltered} period={period} filter={filter} bands={bands} thresholdMode={thresholdMode} excludeDormant={excludeDormant} includeZeroSales={includeZeroSales}/>}
      {!loading&&tab==='pip'&&<PIPCoachingTab period={period} isSM={isSM} data={data} filter={filter} />}
      {!loading&&tab==='settings'&&isSM&&(
        <>
          <button onClick={()=>setTab(prevTab)} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',marginBottom:12,borderRadius:8,border:`1px solid ${C.gray200}`,background:C.white,color:C.gray600,fontWeight:700,fontSize:12,cursor:'pointer'}}>
            ← Back
          </button>
          <SettingsTab thresholds={thresholds} bands={bands}
            onUpdateThresholds={async t=>{await axios.put('/api/performance/thresholds',t);await fetchData();}}
            onUpdateBands={async b=>{await axios.put('/api/performance/bands',b);await fetchData();}} thresholdMode={thresholdMode} onUpdateMode={setThresholdMode} period={period} />
        </>
      )}
      {!loading&&tab==='scorecards'&&<ScorecardsTab agents={flaggedFiltered} bands={bands} onUpdateBands={async b=>{await axios.put('/api/performance/bands',b);await fetchData();}} isSM={isSM} period={period} filter={filter} thresholdMode={thresholdMode} excludeDormant={excludeDormant} includeZeroSales={includeZeroSales} canAddNote={can('appraisals.edit')==='true'} storeTargets={data?.storeTargets||{}} daysGone={data?.daysGone||0} workingDays={data?.workingDays||0}/>}
    </div>
  );
}
